/**
 * MarketStore — Zustand store fed by HydraClient
 *
 * Hydra sends two ticker formats:
 *   Kalshi:      KXNBAGAME-26APR04DETPHI-DET  (eventTicker: KXNBAGAME-26APR04DETPHI)
 *   Polymarket:  PM-nba-det-phi-2026-04-04     (moneyline)
 *                PM-nba-det-phi-2026-04-04-spread-home-1pt5
 *                PM-nba-det-phi-2026-04-04-total-139pt5
 *
 * Performance: Maps are mutated in-place. A `version` counter is bumped on a
 * RAF-throttled schedule so React re-renders at ~display refresh rate, not per-message.
 */

import { create } from 'zustand';
import type { HydraClient, HydraConsensus, HydraMarket, HydraOrderbook, HydraPriceLevel } from '../clients/HydraClient';
import { getTeamDisplayName } from '../lib/kalshiTicker/teamNames';
import { isDrawOutcome } from '../lib/kalshiTicker/helpers';
import type { GameRow, VenueBook, Sport, MarketType, BookLevel, SeriesEvent, SeriesOutcome, MultiOutcomeEvent, DateFilter, StatusFilter, GameEventRow, SpreadLine, TotalLine, PeriodMoneyline, Period } from '../views/ValueDashboard/types';
import { MULTI_OUTCOME_SPORTS } from '../views/ValueDashboard/types';
import { matchPolySpread } from './spreadMatch';
import { depthKey, pruneDepthVelocity, recordDepthSample } from '../components/cards/bookDepthVelocity';

// NOTE: the markets feed is deliberately BROAD — the per-view sport-needs
// narrowing (setSportNeeds / ALL_SPORTS / sportToHydraCode) was removed
// 2026-06-12: it made every sport-chip toggle pay a wire round trip +
// snapshot for data the store didn't hold. See workers/sportFilter.ts.

// ── Ticker parsing ───────────────────────────────────────────────

interface ParsedTicker {
  source: 'kalshi' | 'polymarket';
  sport: Sport;
  date: string;
  awayCode: string;
  homeCode: string;
  outcomeCode: string;
  side: 'away' | 'home';
  /** 'btts' = both-teams-to-score (soccer Game Lines), a single binary yes/no
   *  market per game/period (outcome code 'BTTS', no team). Surfaced via
   *  getBtts; never enters the spread/total/moneyline selectors. */
  marketType: 'moneyline' | 'spread' | 'total' | 'series' | 'btts';
  seriesYear?: string;   // only set when marketType === 'series'
  /**
   * Set on KX{NBA|NHL|MLB}SERIES-… playoff-series-winner tickers. Surfaces
   * alongside per-day moneylines (marketType='moneyline') so the round-N
   * series sits inline at the top of the moneyline view; the date field
   * is a sort-only sentinel since the market spans many calendar days.
   */
  seriesRound?: number;
  /**
   * Set on Kalshi spread tickers (KX*SPREAD-…-{TEAM}{BUCKET}). The bucket is
   * the minimum *integer* margin the team needs for YES to win, i.e.
   * `STL3` resolves YES when STL wins by 3 or more — covers a (bucket − 0.5)
   * line. Line magnitude = floorStrike when hydra forwards it, else
   * `bucket − 0.5` fallback.
   */
  spreadTeamCode?: string;
  spreadBucket?: number;
  /**
   * Set on Kalshi total tickers (KX*TOTAL-…-{STRIKE}). One ticker per
   * strike. YES resolves when total runs/points reach the strike, so the
   * binary "Over X.5" line is `strike − 0.5`. Line value = floorStrike when
   * forwarded, else `strike − 0.5`.
   */
  totalStrike?: number;
  /**
   * Set on Polymarket spread/total slugs whose suffix carries the line
   * (e.g. `-spread-home-1pt5`, `-total-139pt5`). `pt` → `.` in the slug.
   */
  polyLine?: number;
  polySpreadSide?: 'home' | 'away';
  /**
   * Scope of play. 'full' (default / full game) or a sub-period. NBA 1st half
   * ('1H') is the only period supported today; everything else parses as
   * 'full'. Set from the ticker (Kalshi KXNBA1H… prefix / Polymarket -1h-
   * slug segment). The full-game selectors (getGames / getGameEvents) gate to
   * period === 'full' so period markets never pollute the full-game rows;
   * period lines are surfaced via getGameEvents(…, period) / getPeriodMoneyline.
   */
  period: Period;
  /** True only for the Kalshi 1H winner's TIE outcome (KXNBA1HWINNER-…-TIE),
   *  which is a third moneyline outcome with no team. */
  isTie?: boolean;
}

const MONTHS: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

const SPORT_NORMALIZE: Record<string, Sport> = {
  NBA: 'nba', WNBA: 'wnba', NHL: 'nhl', CBB: 'cbb', CFB: 'cfb', MLB: 'mlb', ATP: 'atp', WTA: 'wta', UFC: 'ufc',
  nba: 'nba', wnba: 'wnba', nhl: 'nhl', cbb: 'cbb', cfb: 'cfb', mlb: 'mlb', atp: 'atp', wta: 'wta', ufc: 'ufc',
  ESPORTS: 'esports', BOXING: 'boxing', CRICKET: 'cricket', SOCCER: 'soccer',
  // ITF feeds both tours and Polymarket's `itf` slug carries no gender. Map it
  // to atp so parsePolyTicker doesn't drop the market (else it returns null and
  // never enters g1); getGames then routes women's ITF to the wta tab via its
  // Kalshi pair's tour. Hydra's Kalshi ITF is already folded to ATP/WTA.
  itf: 'atp', ITF: 'atp',
  esports: 'esports', boxing: 'boxing', cricket: 'cricket', soccer: 'soccer',
  // Polymarket soccer slugs carry the COMPETITION code in the sport segment
  // (PM-fifwc-… / PM-fif-…), not "soccer". Map the registered competitions
  // onto the soccer tab so parsePolyTicker resolves both the 3-way moneyline
  // and the full-game spread/total slugs. FIFA World Cup (`fifwc`) is the live
  // rollout; International Friendlies (`fif`) is parked server-side but mapped
  // here for parity with the Kalshi series list (UEFA CL is Kalshi-only). The
  // moneyline still keys its outcome off market.yesOutcome in
  // getMultiOutcomeEvents — parsePolyTicker's away-anchored outcomeCode is a
  // lower-priority fallback — so this doesn't change 3-way collapse behavior.
  fifwc: 'soccer', fif: 'soccer',
};

function parseKalshiTicker(ticker: string, eventTicker: string): ParsedTicker | null {
  // Playoff series-winner shape: KX{NBA|NHL|MLB}SERIES-{YY}{TEAM1}{TEAM2}R{ROUND}-{OUTCOME}
  // e.g. KXNBASERIES-26PHIBOSR1-BOS. One ticker per outcome team. Surfaces
  // inline at the top of the moneyline view, regardless of the calendar day
  // selected — it's a series-level market with no per-day association.
  // Matched first so it doesn't get caught by the championship match below
  // (which is more permissive on the prefix). Team codes are variable
  // length (NHL has 2- and 3-letter codes; MLB/NBA are 3) so we use the
  // outcome team to determine the split point.
  const seriesRoundMatch = ticker.match(
    /^KX(NBA|WNBA|NHL|MLB)SERIES-(\d{2})([A-Z]+)R(\d)-([A-Z0-9]+)$/,
  );
  if (seriesRoundMatch) {
    const [, sportLetters, year, teamsPart, round, outcomeCode] = seriesRoundMatch;
    const sport = SPORT_NORMALIZE[sportLetters];
    if (sport) {
      // Outcome team must appear at the start or end of the teams chunk;
      // its position picks the split. (Same trick as the per-day parser.)
      let team1: string | null = null;
      let team2: string | null = null;
      if (teamsPart.startsWith(outcomeCode)) {
        team1 = outcomeCode;
        team2 = teamsPart.slice(outcomeCode.length);
      } else if (teamsPart.endsWith(outcomeCode)) {
        team1 = teamsPart.slice(0, teamsPart.length - outcomeCode.length);
        team2 = outcomeCode;
      }
      if (team1 && team2) {
        return {
          source: 'kalshi', sport,
          // Sentinel date: sorts after any real game date so series rows
          // pile at the bottom of the moneyline list (per-day games on top).
          // Round number is the tiebreaker so R1 sits above R2, etc.
          date: `9999-${round}-${year}`,
          awayCode: team1, homeCode: team2,
          outcomeCode,
          side: outcomeCode === team1 ? 'away' : 'home',
          marketType: 'moneyline',
          seriesYear: year,
          seriesRound: parseInt(round, 10),
          period: 'full',
        };
      }
    }
  }

  // Series (championship) shape: KXNBA-26-LAL / KXNHL-26-EDM / KXMLB-26-SEA
  // Matched on the full ticker (no GAME/MATCH/FIGHT suffix), before the generic path.
  const seriesChampMatch = ticker.match(/^KX([A-Z]+)-(\d{2})-([A-Z0-9]+)$/);
  if (seriesChampMatch) {
    const [, sportLetters, year, outcomeCode] = seriesChampMatch;
    const sport = SPORT_NORMALIZE[sportLetters];
    if (sport) {
      return {
        source: 'kalshi', sport,
        date: `20${year}-01-01`,
        awayCode: outcomeCode, homeCode: '',
        outcomeCode, side: 'away',
        marketType: 'series',
        seriesYear: year,
        period: 'full',
      };
    }
  }

  // Fall back to deriving the event ticker from the ticker itself when hydra
  // didn't send one. moneyline markets reliably carry eventTicker but
  // spread/total frames have been observed without it. Stripping the last
  // dash-segment off the market ticker reproduces the event ticker.
  const derivedEventTicker = eventTicker || ticker.replace(/-[^-]+$/, '');
  // Series names may contain digits (KXCS2GAME, KXDOTA2GAME, KXWT20MATCH) and
  // boxing has no GAME/MATCH/FIGHT suffix (KXBOXING). The sportMap below is the
  // real gate — unknown series fall out there.
  // WINNER is included for NBA 1st-half period moneylines (KXNBA1HWINNER);
  // BTTS for soccer both-teams-to-score; a bare 1H|2H suffix for soccer's
  // period moneyline (KXWC1H/KXWC2H end at the period token — no bet-type after);
  // and a bare F5 suffix for MLB's First-5-Innings moneyline (KXMLBF5, likewise
  // no bet-type after). The `-` anchor forces the right split (KXWC1HSPREAD →
  // SPREAD, not 1H), and the sportMap gate below still drops any non-allowlisted
  // series.
  const seriesMatch = derivedEventTicker.match(/^(KX[A-Z0-9]+(?:GAME|MATCH|FIGHT|SPREAD|TOTAL|WINNER|BTTS|F5|1H|2H)|KXBOXING)-(.+)$/);
  if (!seriesMatch) return null;
  const [, series, rest] = seriesMatch;

  const sportMap: Record<string, Sport> = {
    KXNBAGAME: 'nba', KXWNBAGAME: 'wnba', KXNHLGAME: 'nhl', KXNCAAMBGAME: 'cbb',
    KXMLBGAME: 'mlb', KXATPMATCH: 'atp', KXWTAMATCH: 'wta',
    KXUFCFIGHT: 'ufc',
    KXBOXING: 'boxing',
    KXCS2GAME: 'esports', KXDOTA2GAME: 'esports', KXVALORANTGAME: 'esports', KXLOLGAME: 'esports',
    KXWT20MATCH: 'cricket',
    KXNCAAFGAME: 'cfb',
    // ITF tennis folds into the ATP (men) / WTA (women) tabs.
    KXITFMATCH: 'atp', KXITFWMATCH: 'wta',
    // Soccer (3-way). Mapped so the internal parser tags sport='soccer', but
    // getGames early-returns for multi-outcome sports — these flow through
    // getMultiOutcomeEvents, not the binary GameRow path.
    KXUECLGAME: 'soccer', KXWCGAME: 'soccer', KXINTLFRIENDLYGAME: 'soccer',
    // Soccer FULL-GAME spread + total. Each line is an ordinary 2-way binary
    // ("Ivory Coast wins by over 1.5 goals" / "Over 0.5 goals scored"), so they
    // flow through the SAME sport-generic getGameEvents path NBA uses — NOT the
    // 3-way moneyline path. FIFA World Cup (KXWCSPREAD/KXWCTOTAL) is verified
    // live; the friendlies + UECL variants mirror their GAME entries for parity
    // (the sportMap gate silently drops them if Kalshi never lists them).
    KXWCSPREAD: 'soccer', KXWCTOTAL: 'soccer',
    KXUECLSPREAD: 'soccer', KXUECLTOTAL: 'soccer',
    KXINTLFRIENDLYSPREAD: 'soccer', KXINTLFRIENDLYTOTAL: 'soccer',
    // Soccer Game Lines — period (1H/2H) + BTTS. The period token is derived
    // below; getGames / getGameEvents gate to period==='full', so periods never
    // enter the full-game rows — they surface through the period selectors.
    // Period moneyline (KXWC1H/KXWC2H, 3-way incl. TIE) parses via getPeriodMoneyline;
    // BTTS (both teams to score — a binary yes/no per game/period) via getBtts.
    // Kalshi-only in Phase 1 (Poly's halftime-result/btts grammar is a follow-up).
    // WC verified live; UECL/INTLFRIENDLY mirror for parity (gate drops unlisted).
    KXWCBTTS: 'soccer', KXWC1H: 'soccer', KXWC2H: 'soccer',
    KXWC1HSPREAD: 'soccer', KXWC2HSPREAD: 'soccer',
    KXWC1HTOTAL: 'soccer', KXWC2HTOTAL: 'soccer',
    KXWC1HBTTS: 'soccer', KXWC2HBTTS: 'soccer',
    KXUECLBTTS: 'soccer', KXUECL1H: 'soccer', KXUECL2H: 'soccer',
    KXUECL1HSPREAD: 'soccer', KXUECL2HSPREAD: 'soccer',
    KXUECL1HTOTAL: 'soccer', KXUECL2HTOTAL: 'soccer',
    KXUECL1HBTTS: 'soccer', KXUECL2HBTTS: 'soccer',
    KXINTLFRIENDLYBTTS: 'soccer', KXINTLFRIENDLY1H: 'soccer', KXINTLFRIENDLY2H: 'soccer',
    KXINTLFRIENDLY1HSPREAD: 'soccer', KXINTLFRIENDLY2HSPREAD: 'soccer',
    KXINTLFRIENDLY1HTOTAL: 'soccer', KXINTLFRIENDLY2HTOTAL: 'soccer',
    KXINTLFRIENDLY1HBTTS: 'soccer', KXINTLFRIENDLY2HBTTS: 'soccer',
    KXNBASPREAD: 'nba', KXWNBASPREAD: 'wnba', KXNHLSPREAD: 'nhl', KXNCAAMBSPREAD: 'cbb',
    KXMLBSPREAD: 'mlb', KXATPSPREAD: 'atp', KXWTASPREAD: 'wta',
    KXNBATOTAL: 'nba', KXWNBATOTAL: 'wnba', KXNHLTOTAL: 'nhl', KXNCAAMBTOTAL: 'cbb',
    KXMLBTOTAL: 'mlb', KXATPTOTAL: 'atp', KXWTATOTAL: 'wta',
    // NBA period markets — 1st half + single quarters. The period token is
    // derived below; getGames / getGameEvents gate to period==='full' so these
    // never enter the full-game rows — they surface only through the period
    // selectors (Game Lines UI). Quarters are Kalshi-only (no Poly quarter
    // markets); there is no quarter WINNER (Kalshi lists only 1H winner).
    KXNBA1HSPREAD: 'nba', KXNBA1HTOTAL: 'nba', KXNBA1HWINNER: 'nba',
    KXNBA1QSPREAD: 'nba', KXNBA2QSPREAD: 'nba', KXNBA3QSPREAD: 'nba', KXNBA4QSPREAD: 'nba',
    KXNBA1QTOTAL: 'nba', KXNBA2QTOTAL: 'nba', KXNBA3QTOTAL: 'nba', KXNBA4QTOTAL: 'nba',
    // WNBA period markets — 1st half only (Kalshi lists no WNBA quarters).
    KXWNBA1HSPREAD: 'wnba', KXWNBA1HTOTAL: 'wnba', KXWNBA1HWINNER: 'wnba',
    // MLB First 5 Innings (F5) — moneyline (3-way incl. TIE), spread, total. The
    // moneyline series is the BARE KXMLBF5 (no GAME suffix — unlike NBA's
    // KXNBA1HWINNER); spread/total append SPREAD/TOTAL. The period token is
    // derived below; getGames / getGameEvents gate to period==='full' so F5
    // never enters the full-game rows — it surfaces only through the Game Lines
    // period selectors. Kalshi-only (Polymarket lists no inning markets); F5 has
    // no F3/F7 siblings.
    KXMLBF5: 'mlb', KXMLBF5SPREAD: 'mlb', KXMLBF5TOTAL: 'mlb',
  };
  const sport = sportMap[series];
  if (!sport) return null;

  // Period markets. The period token (1H/2H, F5, or 1Q–4Q) sits right before the
  // SPREAD/TOTAL/WINNER/BTTS suffix in the series (KXNBA1QTOTAL, KXNBA1HWINNER,
  // KXWC1HBTTS, KXMLBF5TOTAL) — OR is the whole suffix for a bare period
  // moneyline (soccer's KXWC1H/KXWC2H and MLB's KXMLBF5 end at the period token,
  // no bet-type after). KXNBA1HWINNER is the 3-way period moneyline (outcome is a
  // team OR 'TIE'); it ends in none of SPREAD/TOTAL/BTTS so it falls through to
  // 'moneyline' below, as do bare KXWC1H / KXMLBF5. Quarters never carry
  // WINNER/BTTS. Mirrors hydra's grammar.
  const periodMatch = series.match(/(1H|2H|F5|[1-4]Q)(?:SPREAD|TOTAL|WINNER|BTTS)?$/);
  const period: Period = (periodMatch?.[1] as Period | undefined) ?? 'full';
  const marketType: 'moneyline' | 'spread' | 'total' | 'btts' =
    series.endsWith('SPREAD') ? 'spread'
      : series.endsWith('TOTAL') ? 'total'
        : series.endsWith('BTTS') ? 'btts'
          : 'moneyline';
  // UFC FIGHT, tennis MATCH, and all GAME tickers parse as moneyline (1v1 outcome).

  // MLB tickers embed an HHMM start time between the date and the team codes
  // (e.g. KXMLBGAME-26APR131610HOUSEA-SEA where 1610 = 16:10 ET).
  const dateMatch = rest.match(/^(\d{2})([A-Z]{3})(\d{2})(\d{4})?([A-Z].+)$/);
  if (!dateMatch) return null;
  const [, yy, mmm, dd, , teamsPart] = dateMatch;
  const mm = MONTHS[mmm];
  if (!mm) return null;
  const date = `20${yy}-${mm}-${dd}`;

  const tickerParts = ticker.split('-');
  const outcomeCode = tickerParts[tickerParts.length - 1];
  // For moneyline the outcome IS the team code; for spread the team is the
  // leading letters before the bucket digits; total outcomes are pure
  // digits so we fall back to mid-split. Using the team-only prefix to
  // find position in teamsPart correctly handles variable-length codes
  // like MLB's "AZ" alongside "TEX" (teamsPart "AZTEX" — a naïve ceil/2
  // split would yield "AZT"/"EX", which the canonical override would
  // then mask but leave a broken raw teamCode comparison downstream).
  let teamCandidate: string | null = null;
  if (marketType === 'spread') {
    const m = outcomeCode.match(/^([A-Z]+)\d+$/);
    teamCandidate = m ? m[1] : null;
  } else if (marketType === 'moneyline') {
    teamCandidate = outcomeCode;
  }
  let awayCode: string, homeCode: string;
  if (teamCandidate) {
    const idx = teamsPart.indexOf(teamCandidate);
    if (idx === 0) {
      awayCode = teamCandidate;
      homeCode = teamsPart.slice(teamCandidate.length);
    } else if (idx > 0) {
      awayCode = teamsPart.slice(0, idx);
      homeCode = teamCandidate;
    } else {
      const mid = Math.ceil(teamsPart.length / 2);
      awayCode = teamsPart.slice(0, mid);
      homeCode = teamsPart.slice(mid);
    }
  } else {
    // Total — no team in outcome; fall back to mid-split (works for
    // matchups with same-length codes like ARI/TEX, NYY/BAL).
    const mid = Math.ceil(teamsPart.length / 2);
    awayCode = teamsPart.slice(0, mid);
    homeCode = teamsPart.slice(mid);
  }
  if (!awayCode || !homeCode) return null;

  // Determine side: moneyline outcomes are exact team codes (DET),
  // spread outcomes are team+number (DET1, MIA33), totals are just numbers (215).
  // Spread/total tickers also expose the bucket/strike so getGameEvents can
  // keep ALL per-line tickers per game (getGames collapses them — preserved
  // for backwards-compat with the moneyline path which doesn't need per-line).
  let side: 'away' | 'home';
  let spreadTeamCode: string | undefined;
  let spreadBucket: number | undefined;
  let totalStrike: number | undefined;
  if (marketType === 'total') {
    side = 'away'; // totals aren't team-specific
    const totalMatch = outcomeCode.match(/^(\d+)$/);
    if (totalMatch) totalStrike = Number(totalMatch[1]);
  } else if (marketType === 'btts') {
    side = 'away'; // BTTS is one binary (both teams score) — no team/side/strike.
  } else if (outcomeCode === awayCode) {
    side = 'away';
  } else if (outcomeCode === homeCode) {
    side = 'home';
  } else {
    // Spread: outcome is {TEAM}{BUCKET} like "DET7" / "NYK11" / "KC3".
    const spreadMatch = outcomeCode.match(/^([A-Z]+)(\d+)$/);
    if (spreadMatch) {
      spreadTeamCode = spreadMatch[1];
      spreadBucket = Number(spreadMatch[2]);
    }
    side = (spreadTeamCode ?? outcomeCode).startsWith(awayCode) ? 'away' : 'home';
  }

  return {
    source: 'kalshi', sport, date,
    awayCode: awayCode.toUpperCase(), homeCode: homeCode.toUpperCase(),
    outcomeCode: outcomeCode.toUpperCase(),
    side,
    marketType,
    spreadTeamCode,
    spreadBucket,
    totalStrike,
    period,
    // The TIE outcome is the 3rd leg of a 3-way period moneyline —
    // KXNBA1HWINNER-…-TIE, soccer's KXWC1H/KXWC2H-…-TIE, or MLB's
    // KXMLBF5-…-TIE — no team; getPeriodMoneyline keys on this to place the
    // Tie row.
    isTie: (period === '1H' || period === '2H' || period === 'F5') && marketType === 'moneyline' && outcomeCode.toUpperCase() === 'TIE'
      ? true
      : undefined,
  };
}

function parsePolyTicker(ticker: string): ParsedTicker | null {
  const match = ticker.match(/^PM-([a-z]+)-([a-z]+)-([a-z]+)-(\d{4}-\d{2}-\d{2})(.*)$/);
  if (!match) return null;
  const [, sportRaw, awayRaw, homeRaw, date, suffix] = match;
  const sport = SPORT_NORMALIZE[sportRaw];
  if (!sport) return null;
  // Period markets (Game Lines feature). Hydra flows these on the same feed —
  // e.g. `PM-nba-nyk-sas-2026-06-03-1h-moneyline` / `-1h-spread-home-2pt5` —
  // carrying the FULL-GAME canonicalGameKey, with the period token right after
  // the date. Detect a leading `-1h-` segment → period '1H' and parse the rest
  // (-spread/-total/-moneyline) past it. Unsupported periods (2nd half,
  // quarters) are still dropped so they can't mis-parse as a full-game
  // moneyline and clobber g.polyTicker (the 0cf7549 bug). The full-game
  // selectors gate to period==='full', so 1H never pollutes the full-game row.
  // NOTE: quarters (1q–4q) stay dropped *by design* — Polymarket lists no
  // quarter markets, so this branch only ever sees the irregular-slug case as a
  // defensive backstop; quarter coverage is intentionally Kalshi-only.
  let period: Period = 'full';
  let body = suffix;
  const periodMatch = suffix.match(/^-(1h|2h|[1-4]q)\b/);
  if (periodMatch) {
    if (periodMatch[1] !== '1h') return null; // 2H / quarters not on Polymarket
    period = '1H';
    body = suffix.slice(periodMatch[0].length); // strip "-1h" → "-spread-…" / "-total-…" / "-moneyline"
  }
  const awayCode = codeFromPolySlug(awayRaw, sport);
  const homeCode = codeFromPolySlug(homeRaw, sport);

  // ── Soccer Game Lines: period (1H/2H) result + totals, and BTTS ──────
  // Poly lists these in separate Gamma events with the scope baked into the
  // bet-type segment (not a -1h-/-2h- token), all sharing the game's
  // canonicalGameKey. Map them so getPeriodMoneyline / getGameEvents(period) /
  // getBtts collapse the Poly book onto the matching Kalshi line. Full-game
  // 1X2 (-can/-qat/-draw), -spread, -total fall through to the logic below.
  if (sport === 'soccer') {
    // BTTS — one binary per scope. Full game `-btts`, halves `-btts-first-half`
    // / `-btts-second-half`. (Also fixes full-game `-btts` previously parsing as
    // a moneyline and polluting the 3-way.)
    if (suffix === '-btts' || suffix === '-btts-first-half' || suffix === '-btts-second-half') {
      const p: Period = suffix === '-btts-first-half' ? '1H' : suffix === '-btts-second-half' ? '2H' : 'full';
      return {
        source: 'polymarket', sport, date, awayCode, homeCode,
        outcomeCode: 'BTTS', side: 'away', marketType: 'btts', period: p,
      };
    }
    // 3-way half result: `-halftime-result-{away|home|draw}` (1H),
    // `-second-half-result-{away|home|draw}` (2H). Resolve to a TEAM code by slug
    // POSITION so getPeriodMoneyline pairs by code (the Poly↔Kalshi home/away
    // label disagreement then doesn't matter). NOTE: verified live across games,
    // Poly's result `-home` is the FIRST slug team (t1 = our awayCode) and
    // `-away` is the second (t2 = homeCode) — the opposite of the label. (Alias
    // countries like Poly che ↔ Kalshi SUI are a known gap — codes won't match.)
    const resultMatch = suffix.match(/^-(halftime|second-half)-result-(away|home|draw)$/);
    if (resultMatch) {
      const p: Period = resultMatch[1] === 'halftime' ? '1H' : '2H';
      const role = resultMatch[2];
      const isTie = role === 'draw';
      const teamCode = role === 'home' ? awayCode : homeCode; // home→t1, away→t2
      return {
        source: 'polymarket', sport, date, awayCode, homeCode,
        outcomeCode: isTie ? 'DRAW' : teamCode,
        side: isTie || teamCode === awayCode ? 'away' : 'home',
        marketType: 'moneyline', period: p,
        isTie: isTie ? true : undefined,
      };
    }
    // Half totals: `-first-half-total-Npt5` (1H), `-second-half-total-Npt5` (2H).
    const halfTotal = suffix.match(/^-(first|second)-half-total-(\d+(?:pt\d+)?)$/);
    if (halfTotal) {
      const p: Period = halfTotal[1] === 'first' ? '1H' : '2H';
      return {
        source: 'polymarket', sport, date, awayCode, homeCode,
        outcomeCode: awayCode, side: 'away',
        marketType: 'total', period: p,
        polyLine: Number(halfTotal[2].replace('pt', '.')),
      };
    }
  }

  let marketType: 'moneyline' | 'spread' | 'total' = 'moneyline';
  let polyLine: number | undefined;
  let polySpreadSide: 'home' | 'away' | undefined;
  if (body.startsWith('-spread')) {
    marketType = 'spread';
    // `-spread-home-1pt5` or `-spread-away-1pt5`. `pt` is Polymarket's
    // slug-safe stand-in for the decimal point.
    const spreadMatch = body.match(/^-spread-(home|away)-(\d+(?:pt\d+)?)/);
    if (spreadMatch) {
      polySpreadSide = spreadMatch[1] as 'home' | 'away';
      // Polymarket labels SOCCER spreads with the first slug team as "home"
      // (neutral-venue convention) — the inverse of Kalshi/g1's first=away, so
      // its `-spread-home` book is actually the g1 AWAY team's spread, and the
      // side===favored pairing in getGameEvents would attach it to the wrong
      // row. US sports align (MLB `-spread-home` = the home team), so flip
      // soccer only. Verified live: SCO/MAR inverted, BOS/SEA aligned.
      if (sport === 'soccer') polySpreadSide = polySpreadSide === 'home' ? 'away' : 'home';
      polyLine = Number(spreadMatch[2].replace('pt', '.'));
    }
  } else if (body.startsWith('-total')) {
    marketType = 'total';
    // `-total-139pt5` → 139.5
    const totalMatch = body.match(/^-total-(\d+(?:pt\d+)?)/);
    if (totalMatch) polyLine = Number(totalMatch[1].replace('pt', '.'));
  }
  // body === '-moneyline' (or '') → marketType stays 'moneyline'.

  return {
    source: 'polymarket', sport, date,
    awayCode, homeCode,
    outcomeCode: awayCode, side: 'away',
    marketType,
    polyLine,
    polySpreadSide,
    period,
  };
}

function codeFromPolySlug(raw: string, sport: Sport): string {
  const upper = raw.toUpperCase();
  // Polymarket tennis slugs use truncated surnames (`berrett`, `comesan`);
  // Kalshi uses compact player codes (`BER`, `COM`). First-three-letter
  // canonicalization lets the same match row attach across venues without a
  // per-player registry.
  if (sport === 'atp' || sport === 'wta') return upper.slice(0, 3);
  return upper;
}

interface SeriesScoreTickerInfo {
  teamCode: string;
  winnerWins: number;
  loserWins: number;
  round?: number;
  year: string;
  teamChunk: string;
}

function inferSeriesRoundFromChunk(teamChunk: string): number | undefined {
  const roundMatch = teamChunk.match(/R(\d+)$/);
  if (roundMatch) return Number(roundMatch[1]);
  if (/(?:ECF|WCF|ALCS|NLCS|LCS)$/.test(teamChunk)) return 3;
  if (/(?:FIN|WS)$/.test(teamChunk)) return 4;
  return undefined;
}

function stripSeriesStageSuffix(teamChunk: string): string {
  const roundStripped = teamChunk.replace(/R\d+$/, '');
  if (roundStripped !== teamChunk) return roundStripped;
  const suffixes = ['ALCS', 'NLCS', 'ALDS', 'NLDS', 'WCF', 'ECF', 'FIN', 'EAST', 'WEST', 'LCS', 'LDS', 'WC'];
  for (const suffix of suffixes) {
    if (teamChunk.endsWith(suffix)) return teamChunk.slice(0, -suffix.length);
  }
  return teamChunk;
}

function orderedSeriesTeamsFromScoreTicker(
  teamChunk: string,
  teams: readonly [string, string],
): [string, string] | null {
  const stripped = stripSeriesStageSuffix(teamChunk);
  const [a, b] = teams;
  if (stripped === `${a}${b}`) return [a, b];
  if (stripped === `${b}${a}`) return [b, a];
  return null;
}

function parseKalshiSeriesScoreTicker(ticker: string): SeriesScoreTickerInfo | null {
  const match = ticker.match(
    /^KX(NBA|WNBA|NHL|MLB)SERIESSCORE-(\d{2})([A-Z0-9]+)-([A-Z]+)(\d)(\d)$/,
  );
  if (!match) return null;
  const [, , year, teamChunk, teamCode, winnerWinsRaw, loserWinsRaw] = match;
  const winnerWins = Number(winnerWinsRaw);
  const loserWins = Number(loserWinsRaw);
  if (!Number.isFinite(winnerWins) || !Number.isFinite(loserWins)) return null;
  return {
    teamCode,
    winnerWins,
    loserWins,
    round: inferSeriesRoundFromChunk(teamChunk),
    year,
    teamChunk,
  };
}

function seriesScoreIsCurrentClincher(
  score: SeriesScoreTickerInfo,
  yesTeam: string,
  seriesTeams: readonly [string, string],
  seriesWins: Record<string, number> | undefined,
  winsToClinch: number | undefined,
): boolean {
  if (!seriesWins || winsToClinch == null) return false;
  const opponent = seriesTeams[0] === yesTeam ? seriesTeams[1] : seriesTeams[1] === yesTeam ? seriesTeams[0] : null;
  if (!opponent) return false;
  return (
    score.winnerWins === winsToClinch
    && score.loserWins === (seriesWins[opponent] ?? -1)
    && (seriesWins[yesTeam] ?? -1) === winsToClinch - 1
  );
}

function escapeRegExp(raw: string): string {
  return raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isDirectKalshiTeamSeriesMarket(market: HydraMarket): boolean {
  if (market.source !== 'kalshi' || !market.canonicalYesTeam) return false;
  const seriesTicker = market.seriesTicker ?? market.ticker.split('-')[0];
  if (!seriesTicker) return false;
  return new RegExp(`^${escapeRegExp(seriesTicker)}-\\d{2}-[A-Z0-9]+$`).test(market.ticker);
}

/**
 * Pull the canonical line value out of a Kalshi market subtitle. Hydra
 * forwards Kalshi's `yes_sub_title` on every spread + total frame; it
 * reads either "Montreal wins by over 2.5 goals" (spread) or "Over
 * 203.5 points scored" (total). One regex covers both shapes. Falls
 * back to `title` for the spread case to tolerate pre-subtitle hydra
 * builds during deploy. Returns null when nothing parseable is present.
 */
function parseLineFromSubtitle(subtitle: string | undefined): number | null {
  if (!subtitle) return null;
  // Spread / total form: "... over N (runs|points|goals|games|rounds)".
  // Anchoring to the unit avoids accidental matches against arbitrary
  // "over N" text fragments elsewhere in the subtitle (e.g. a total
  // subtitle accidentally attached to a spread market, or "over $N
  // wagered" promo copy).
  const overUnder = subtitle.match(
    /\b(?:over|under) (\d+(?:\.\d+)?) (?:runs?|points?|goals?|games?|rounds?)\b/i,
  );
  if (overUnder) {
    const n = Number(overUnder[1]);
    return Number.isFinite(n) ? n : null;
  }
  // MLB-style spread copy: "wins by N or more runs". "N or more" with
  // the .5-line convention means the team covers a line of N − 0.5
  // (winning by 2 covers 1.5, winning by 3 covers 2.5). Returning the
  // adjusted value keeps us consistent with the bucket-fallback math
  // (bucket-0.5) that runs when no subtitle is present at all.
  const orMore = subtitle.match(
    /\bby (\d+) or more (?:runs?|points?|goals?|games?|rounds?)\b/i,
  );
  if (orMore) {
    const n = Number(orMore[1]);
    return Number.isFinite(n) ? n - 0.5 : null;
  }
  return null;
}

function parseTicker(market: HydraMarket): ParsedTicker | null {
  if (market.source === 'polymarket' || market.ticker.startsWith('PM-')) {
    return parsePolyTicker(market.ticker);
  }
  // Series-winner and championship futures don't necessarily carry an
  // eventTicker from hydra, so fall through to the Kalshi parser as long
  // as it's a KX-prefixed ticker. The per-day game branch inside still
  // requires eventTicker; the SERIES / KXNBA-{YY}-{TEAM} branches don't.
  if (market.ticker.startsWith('KX')) {
    return parseKalshiTicker(market.ticker, market.eventTicker ?? '');
  }
  return null;
}

function normalizeOutcomeCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const upper = code.trim().toUpperCase();
  if (!upper) return null;
  return isDrawOutcome(upper) ? 'DRAW' : upper;
}

/**
 * Canonical team/outcome code hydra reconciles onto a Polymarket soccer frame's
 * `yesOutcome` — e.g. it maps the slug spelling `kr` to the FIFA code `KOR`, so
 * period markets pair by code instead of the alias-prone slug. Returns null when
 * the frame still carries a positional value (`HOME`/`AWAY`, pre-reconcile) or
 * none, so callers fall back to the slug-derived parse. 'DRAW' passes through.
 */
function polyCanonicalOutcome(m: HydraMarket): string | null {
  const yo = normalizeOutcomeCode(m.yesOutcome);
  if (yo && yo !== 'HOME' && yo !== 'AWAY') return yo;
  return normalizeOutcomeCode(m.canonicalYesTeam);
}

function sportFromMarket(market: HydraMarket, parsed: ParsedTicker | null): Sport | null {
  if (parsed?.sport) return parsed.sport;
  const key = market.canonicalGameKey ?? market.canonicalSeriesKey ?? null;
  if (key) {
    const i = key.indexOf('|');
    if (i > 0) {
      const fromKey = SPORT_NORMALIZE[key.slice(0, i)];
      if (fromKey) return fromKey;
    }
  }
  return market.sport ? (SPORT_NORMALIZE[market.sport] ?? null) : null;
}

function codesFromCanonicalGameKey(key: string | null | undefined): {
  date: string | null;
  awayCode: string;
  homeCode: string;
} | null {
  if (!key) return null;
  const parts = key.split('|');
  if (parts.length < 4) return null;
  return {
    date: parts[1] || null,
    awayCode: parts[2]?.toUpperCase() ?? '',
    homeCode: parts[3]?.toUpperCase() ?? '',
  };
}

function soccerPolyOutcomeFromTicker(ticker: string): string | null {
  const match = ticker.match(/^PM-[a-z0-9]+-[a-z0-9]+-[a-z0-9]+-\d{4}-\d{2}-\d{2}-(?:moneyline-)?([a-z0-9]+)$/i);
  return normalizeOutcomeCode(match?.[1]);
}

// ── Helpers ──────────────────────────────────────────────────────

/** Format an epoch ms to "H:MMa" / "H:MMp" in the **browser's local
 *  timezone**. Empty string when the input is null/non-finite. The narrow
 *  Date column on GameTable can't fit "AM/PM" without wrapping, so the
 *  single-char p/a form is used. The user's browser zone is the right
 *  default for a personal trading UI. */
const _LOCAL_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const _LOCAL_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function parseStartTimeMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function formatStartDateLocal(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms)) return null;
  const parts = _LOCAL_DATE_FMT.formatToParts(new Date(ms));
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : null;
}

function formatStartTimeLocal(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  const parts = _LOCAL_TIME_FMT.formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const dayPeriod = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toLowerCase();
  const ampm = dayPeriod.startsWith('p') ? 'p' : 'a';
  return hour && minute ? `${hour}:${minute}${ampm}` : '';
}

function toBookLevels(levels: HydraPriceLevel[]): BookLevel[] {
  return levels
    .map((l) => ({ priceCents: l.price, size: l.size }))
    .sort((a, b) => b.priceCents - a.priceCents); // descending: best bid first
}

/** Status values hydra emits when a market reaches its terminal state.
 *  Hydra broadcasts the transition once per ticker then prunes ~5 min later
 *  (see HYDRA_API.md "Terminal state"). Used to drive `gameState='final'`
 *  on GameRow and the FINAL row treatment in GameTable. */
export const TERMINAL_STATUSES = new Set(['closed', 'settled', 'finalized', 'determined']);
const TRADABLE_STATUSES = new Set(['open', 'active']);

/** Per-sport regulation-plus-buffer game length in ms. Used as the
 *  time-based fallback in `deriveGameState` so a yesterday game whose
 *  Kalshi market is still nominally 'open' (Kalshi often lags on the
 *  'open'→'settled' transition by hours) collapses to FINAL anyway. The
 *  values are generous on purpose — better to leave a live row up an
 *  extra 30 min than to mark a real OT game final mid-flight. */
const SPORT_GAME_DURATION_MS: Record<string, number> = {
  nba: 3 * 3_600_000,
  wnba: 2.5 * 3_600_000,
  nhl: 3 * 3_600_000,
  mlb: 4 * 3_600_000,
  cbb: 2.5 * 3_600_000,
  cfb: 4.5 * 3_600_000,
  atp: 4 * 3_600_000,
  wta: 3 * 3_600_000,
  ufc: 4 * 3_600_000,
  boxing: 6 * 3_600_000,
  esports: 4 * 3_600_000,
  cricket: 4.5 * 3_600_000,
  soccer: 2.5 * 3_600_000,
  nfl: 3.5 * 3_600_000,
};
const GAME_FINAL_GRACE_MS = 30 * 60_000;

function deriveGameState(
  statuses: string[],
  isSeries: boolean,
  startTimeMs: number | null,
  nowMs: number,
  sport: string | null = null,
): 'live' | 'pre' | 'final' {
  if (statuses.length === 0) return 'pre';
  // All non-empty statuses terminal → final. Series rows never reach
  // final because the underlying tickers transition independently from
  // the row's lifetime; treat any non-terminal status as keeping the
  // row live/pre.
  if (!isSeries && statuses.every((s) => TERMINAL_STATUSES.has(s))) return 'final';
  // ANY-terminal acceleration: Polymarket typically resolves before
  // Kalshi finalizes (Kalshi waits on staff settlement). If even one
  // venue has marked the market terminal AND we've crossed the
  // scheduled event start, trust that signal — the game is over. Gate on
  // startTimeMs to avoid false-positives from pre-game stale 'closed'
  // states (e.g. a series-clinched team's losing ticker). When start
  // time is unknown, we DON'T fire this — fall through to the time
  // and all-terminal checks instead.
  if (!isSeries && startTimeMs != null && nowMs >= startTimeMs) {
    if (statuses.some((s) => TERMINAL_STATUSES.has(s))) return 'final';
  }
  // Time-based final coercion: Kalshi often leaves yesterday's games
  // in 'open' for hours past their actual end. Past sport duration +
  // grace → trust the clock.
  if (!isSeries && startTimeMs != null && sport) {
    const dur = SPORT_GAME_DURATION_MS[sport];
    if (dur != null && nowMs - startTimeMs > dur + GAME_FINAL_GRACE_MS) {
      return 'final';
    }
  }
  if (statuses.some((s) => TRADABLE_STATUSES.has(s))) {
    // 'open'/'active' means the book is available for trading, not that the
    // game is in progress. Only call it 'live' once we've crossed the known
    // start time. Unknown startTimeMs defaults to 'pre' so the live glow
    // doesn't false-positive on every tradeable book.
    if (startTimeMs != null && nowMs >= startTimeMs) return 'live';
    return 'pre';
  }
  return 'pre';
}

/** Final-game winner derivation from price collapse to 100¢/0¢. Each Kalshi
 *  market is single-team-YES so its lastPrice tells you that team directly;
 *  Polymarket is single-market-YES with the side identified by `yesIsAway`.
 *  Returns null when no source has a definitive 99-or-1¢ resolution
 *  (in-flight finalization, missing lastPrice). */
function deriveFinalWinnerSide(
  kAwayMkt: HydraMarket | null,
  kHomeMkt: HydraMarket | null,
  polyMkt: HydraMarket | null,
  yesIsAwayForPoly: boolean,
): 'away' | 'home' | null {
  if (kAwayMkt?.lastPrice != null) {
    if (kAwayMkt.lastPrice >= 99) return 'away';
    if (kAwayMkt.lastPrice <= 1) return 'home';
  }
  if (kHomeMkt?.lastPrice != null) {
    if (kHomeMkt.lastPrice >= 99) return 'home';
    if (kHomeMkt.lastPrice <= 1) return 'away';
  }
  if (polyMkt?.lastPrice != null) {
    if (polyMkt.lastPrice >= 99) return yesIsAwayForPoly ? 'away' : 'home';
    if (polyMkt.lastPrice <= 1) return yesIsAwayForPoly ? 'home' : 'away';
  }
  return null;
}

/**
 * Pick the highest-price bid level on a side, filtered to "real" bids:
 * size ≥ 1 contracts AND price within `slack`¢ of the last trade. Mirrors
 * the ladder/helpers.bestBidDepthForSide sanity check so depth-velocity
 * samples (and any future consumer here) read the same level the K Q /
 * P Q column does. Operates on the raw HydraPriceLevel shape (`price` /
 * `size`) since this runs at ingest, before VenueBook construction.
 */
function bestRealBidLevel(
  levels: HydraPriceLevel[] | undefined,
  lastTradeCents: number | null,
  slack = 15,
): HydraPriceLevel | null {
  if (!levels || levels.length === 0) return null;
  let best: HydraPriceLevel | null = null;
  for (const lvl of levels) {
    if (lvl.size < 1) continue;
    if (lastTradeCents != null && lvl.price > lastTradeCents + slack) continue;
    if (!best || lvl.price > best.price) best = lvl;
  }
  return best;
}

/**
 * Build a `VenueBook` from a raw `HydraMarket` + optional orderbook frame.
 * Liquidity counts both bid and ask depth so ladder pills can scale against
 * the total $ in book. Volume falls back to summed depth when hydra hasn't
 * sent a market frame yet.
 */
export function buildVenueBookFromMarket(
  mkt: HydraMarket | null,
  book: HydraOrderbook | null,
): VenueBook {
  const price =
    mkt?.yesBid != null && mkt?.yesAsk != null
      ? Math.round((mkt.yesBid + mkt.yesAsk) / 2)
      : mkt?.lastPrice ?? null;
  const bids = book?.yes ?? [];
  const asks = book?.no ?? [];
  const bidLiq = bids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const askLiq = asks.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const liq = bidLiq + askLiq;
  // Per-side resting depth: SpreadsTotalsTable splits its K Vol / P Vol
  // column into O / U (for totals) or Y / N (for spreads) so traders can
  // see liquidity tilt at a glance. Hydra doesn't send traded volume split
  // by side, so depth is the best signal available here. Undefined when
  // there's no orderbook frame yet.
  const yesDepth = book ? bids.reduce((s, l) => s + l.size, 0) : undefined;
  const noDepth = book ? asks.reduce((s, l) => s + l.size, 0) : undefined;
  const bookDepth = (yesDepth ?? 0) + (noDepth ?? 0);
  const vol = mkt?.volume ?? bookDepth;
  // Forward polymarket per-outcome tokens when present. Kalshi markets
  // leave these undefined, which is fine — TradeModal already skips
  // poly-tokenId resolution for kalshi venues. Used by synthesize.ts's
  // spread/total rows where away/home share one underlying book and the
  // YES vs NO split has to come from within the book (the opposite-book
  // moneyline trick doesn't apply when both slots point at the same
  // VenueBook reference).
  return {
    marketTicker: mkt?.ticker ?? '',
    priceCents: price,
    lastPriceCents: mkt?.lastPrice ?? null,
    volume: vol,
    yesVolume: yesDepth,
    noVolume: noDepth,
    liquidity: Math.round(liq),
    yes: toBookLevels(bids),
    no: toBookLevels(asks),
    tokenId: mkt?.yesTokenId,
    noTokenId: mkt?.noTokenId,
    conditionId: mkt?.conditionId,
    negRisk: mkt?.negRisk,
    tickSize: mkt?.tickSize,
  };
}

/**
 * Project a single Polymarket binary moneyline (one slug) onto away/home
 * VenueBooks. The slug's YES outcome is one team (per `canonicalYesTeam`, else
 * default YES=away); that team gets the native book, the other the inverse
 * (price 100−yes, yes/no ladders swapped, outcome tokens swapped). Shared by
 * getGames' full-game moneyline (buildPolyBooks) and getPeriodMoneyline's 1H
 * winner pairing so both invert identically.
 */
function buildPolyMoneylinePair(
  polyMkt: HydraMarket,
  polyBook: HydraOrderbook | null,
  awayCode: string,
): { away: VenueBook; home: VenueBook } {
  const yesIsAway = polyMkt.canonicalYesTeam == null
    ? true
    : polyMkt.canonicalYesTeam === awayCode;
  const slugYesPrice = polyMkt.yesBid != null && polyMkt.yesAsk != null
    ? Math.round((polyMkt.yesBid + polyMkt.yesAsk) / 2) : polyMkt.lastPrice ?? null;
  const slugNoPrice = polyMkt.noBid != null && polyMkt.noAsk != null
    ? Math.round((polyMkt.noBid + polyMkt.noAsk) / 2)
    : (slugYesPrice != null ? 100 - slugYesPrice : null);
  const vol = polyMkt.volume ?? 0;
  const slugYesBids = polyBook?.yes ?? [];
  const slugNoBids = polyBook?.no ?? [];
  const bidLiq = slugYesBids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const askLiq = slugNoBids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const totalLiq = Math.round(bidLiq + askLiq);
  const yesTeamBook: VenueBook = {
    marketTicker: polyMkt.ticker, priceCents: slugYesPrice,
    lastPriceCents: polyMkt.lastPrice ?? null,
    volume: vol, liquidity: totalLiq,
    yes: toBookLevels(slugYesBids), no: toBookLevels(slugNoBids),
    tokenId: polyMkt.yesTokenId, noTokenId: polyMkt.noTokenId,
    conditionId: polyMkt.conditionId, negRisk: polyMkt.negRisk, tickSize: polyMkt.tickSize,
  };
  const otherTeamBook: VenueBook = {
    marketTicker: polyMkt.ticker, priceCents: slugNoPrice,
    lastPriceCents: polyMkt.lastPrice != null ? 100 - polyMkt.lastPrice : null,
    volume: vol, liquidity: totalLiq,
    yes: toBookLevels(slugNoBids), no: toBookLevels(slugYesBids),
    tokenId: polyMkt.noTokenId, noTokenId: polyMkt.yesTokenId,
    conditionId: polyMkt.conditionId, negRisk: polyMkt.negRisk, tickSize: polyMkt.tickSize,
  };
  return yesIsAway
    ? { away: yesTeamBook, home: otherTeamBook }
    : { away: otherTeamBook, home: yesTeamBook };
}

// ── Feed log (ring buffer, last 100 pricing updates) ─────────────

export type FeedLogSource = 'kalshi' | 'polymarket' | 'unabated';

export interface FeedLogEntry {
  ts: number;          // Date.now()
  ticker: string;      // for unabated entries: canonicalGameKey (no Kalshi/Poly ticker exists)
  source: FeedLogSource;
  sport: string;
  type: 'market' | 'orderbook' | 'consensus';
  yesBid?: number;
  yesAsk?: number;
  volume?: number;     // market volume (from HydraMarket.volume)
  bookLevels?: number; // total yes+no levels in orderbook
  // Consensus-only — full-game probability cents for each team, plus the
  // periodTypeId so future in-period updates can be distinguished in the log.
  awayProbCents?: number;
  homeProbCents?: number;
  periodTypeId?: number;
}

const FEED_LOG_MAX = 100;
const _feedLogKalshi: FeedLogEntry[] = [];
const _feedLogPoly: FeedLogEntry[] = [];
const _feedLogUnabated: FeedLogEntry[] = [];

function pushFeedLog(entry: FeedLogEntry) {
  const log =
    entry.source === 'kalshi' ? _feedLogKalshi :
    entry.source === 'polymarket' ? _feedLogPoly :
    _feedLogUnabated;
  log.push(entry);
  if (log.length > FEED_LOG_MAX) log.shift();
}

// ── Mutable data layer (not in Zustand state — no copy overhead) ─

const _markets = new Map<string, HydraMarket>();
const _orderbooks = new Map<string, HydraOrderbook>();

// Browser-console diagnostic: call `__dumpKalshiSpreads('TBNYY')` (or any
// matchup substring) to list every Kalshi spread/total market currently
// in the store whose ticker contains the substring, along with the
// parser's interpretation of it. Helps figure out why a particular
// magnitude / side isn't showing in the chip strip — surfaces whether
// hydra is even sending the ticker we expect, and what marketStore
// thinks the (team, bucket, lineMag, favored) reads as.
if (typeof window !== 'undefined') {
  // @ts-expect-error attaching debug helper to window
  window.__dumpKalshiSpreads = (matchup: string) => {
    const lower = matchup.toUpperCase();
    const rows: Array<Record<string, unknown>> = [];
    for (const [, m] of _markets) {
      if (!m.ticker.startsWith('KX')) continue;
      if (!m.ticker.includes(lower)) continue;
      const parsed = parseTicker(m);
      const subtitleParse = parseLineFromSubtitle(m.subtitle);
      const titleParse = parseLineFromSubtitle(m.title);
      rows.push({
        ticker: m.ticker,
        status: m.status,
        subtitle: m.subtitle,
        title: m.title,
        canonicalGameKey: m.canonicalGameKey,
        parsedMarketType: parsed?.marketType,
        parsedSpreadTeamCode: parsed?.spreadTeamCode,
        parsedSpreadBucket: parsed?.spreadBucket,
        parsedAwayCode: parsed?.awayCode,
        parsedHomeCode: parsed?.homeCode,
        subtitleLine: subtitleParse,
        titleLine: titleParse,
        bucketFallbackLine: parsed?.spreadBucket != null
          ? parsed.spreadBucket - 0.5
          : null,
      });
    }
    // eslint-disable-next-line no-console
    console.table(rows);
    return rows;
  };
}
// Cache of canonicalGameKey → eventStartTime epoch ms. `eventStartTime`
// flows in from Polymarket Gamma, Kalshi milestones, and the Unabated
// game-state poll. Every sibling market sharing the canonical key
// (moneyline legs, spreads, totals, …) reads back the same value.
// Used by lookups like `getEventStartTimeMsByTicker` to give related
// markets an event start without an O(N) scan per query.
//
// Update policy (`updateStartTimeCache` is the only mutator):
//   • Pre-game (now < cachedMs) OR cache empty → last-write-wins from any
//     source. Schedule changes from any feed propagate immediately.
//   • Post game-start (now >= cachedMs) AND Unabated owns the cached
//     value → only Unabated may update. Kalshi/Poly often keep streaming
//     the original scheduled tip after live delays move the real start;
//     Unabated reflects the actual in-progress timing, so it wins.
//   • Post game-start without an Unabated lock → last-write-wins
//     continues, so a late Unabated arrival can take over.
type StartTimeSource = 'kalshi' | 'polymarket' | 'unabated';
const _startTimeMsByGameKey = new Map<string, number>();
const _startSourceByGameKey = new Map<string, StartTimeSource>();

function updateStartTimeCache(
  gameKey: string,
  ms: number,
  source: StartTimeSource,
): boolean {
  if (!Number.isFinite(ms)) return false;
  const cachedMs = _startTimeMsByGameKey.get(gameKey);
  const cachedSrc = _startSourceByGameKey.get(gameKey);
  if (
    cachedMs != null
    && Date.now() >= cachedMs
    && cachedSrc === 'unabated'
    && source !== 'unabated'
  ) {
    return false;
  }
  if (cachedMs === ms && cachedSrc === source) return false;
  _startTimeMsByGameKey.set(gameKey, ms);
  _startSourceByGameKey.set(gameKey, source);
  return true;
}
// Cache of (canonicalGameKey × periodTypeId) → latest Unabated consensus
// snapshot. Fed by the `consensus` WS channel (HYDRA_API.md "Unabated
// consensus update"). Hydra emits on ≥1¢ shift per (game, period) and
// backfills the latest Full Game value per active game on subscribe, so
// `getConsensusFor` returns the live value as soon as the WS hand-shakes
// — no per-game REST poll needed.
//
// Keyed by `${canonicalGameKey}|${periodTypeId}` so in-period values
// (Q1 / inning / set, periodTypeId !== 1) don't clobber the Full Game
// read consumers show by default. `getConsensusFor` filters to
// periodTypeId=1 — when in-period views land, they'll select on the
// period they want from the same cache.
const _consensusByGameKeyAndPeriod = new Map<string, HydraConsensus>();

// Active-sport set from hydra's `activeSports` meta frame (normalized to g1
// Sport ids, unknown wire codes like NFL filtered out). Null until the first
// frame arrives. Holds the last NON-EMPTY set: hydra's cache is cold for
// ~1-2 min after its nightly restart and the frame arrives empty in that
// window — valid data, but blanking the toolbar mid-session helps nobody.
let _activeSports: Set<Sport> | null = null;
const consensusCacheKey = (gameKey: string, periodTypeId: number) =>
  `${gameKey}|${periodTypeId}`;

// Per-ticker last-receive timestamp (Date.now() at ingest). Drives the
// activity-fraction stale check in requestResnapshotIfStale — see
// ACTIVE_FRACTION_THRESHOLD below for why we look at the share of
// active tickers rather than the global lastMessageMs.
const _lastReceiveMs = new Map<string, number>();
// Last ORDERBOOK-frame receive time per ticker, tracked separately from
// _lastReceiveMs (which market + trade frames also bump). The Kalshi
// orderbook-delivery gap freezes the book while market frames keep flowing,
// so a freshness indicator built on _lastReceiveMs would stay green through
// the freeze. This map is the honest "are orderbook frames still arriving"
// signal — read via getOrderbookAgeMs.
const _lastOrderbookMs = new Map<string, number>();

// Stale-feed heuristic. The global `client.lastMessageMs` lies when even
// one ticker keeps ticking (which happens when hydra/upstream throttles
// almost everything but leaves a single feed alive). We instead measure
// "share of tickers that received any frame in the last N seconds":
//   - active = tickers in _markets with _lastReceiveMs > now - maxAgeMs
//   - if active / total < ACTIVE_FRACTION_THRESHOLD → resnapshot
// Threshold deliberately low (10%) so we only trigger when the feed is
// clearly mostly-dead — many markets are legitimately quiet (settled,
// pre-game) so a higher threshold would false-positive on healthy state.
// Min population guards against the early connect window where the
// fraction is meaningless on a few seed tickers.
const ACTIVE_FRACTION_THRESHOLD = 0.10;
const ACTIVE_FRACTION_MIN_POP = 20;
let _dirty = false;
let _rafId: number | null = null;
let _flushFn: (() => void) | null = null;

// Most recent message arrival across any source/ticker. Bumped on every
// ingest and surfaced through the store so React components can render
// "feed is stale" UI without polling HydraClient directly. Stays at 0
// until the first message arrives so consumers can distinguish "never
// connected" from "silent for a long time."
let _globalLastReceiveMs = 0;

// Active HydraClient. Set by `useHydra` on mount, cleared on unmount.
// Views forward their orderbook-ticker needs through `setOrderbookNeeds`
// which proxies to this client so the SharedWorker (or direct-WS path)
// can narrow the upstream orderbook subscription.
let _activeClient: HydraClient | null = null;

// Per-view orderbook ticker needs. Keyed by a view-supplied id
// (e.g. `'value-dashboard'`, `'streamrift'`) so each view can declare
// its needs independently — the store unions across views before
// handing the set off to the client. Keeping this outside Zustand
// state avoids triggering re-renders on need changes.
const _viewOrderbookNeeds = new Map<string, Set<string>>();

function _unionOrderbookNeeds(): Set<string> {
  const union = new Set<string>();
  for (const needs of _viewOrderbookNeeds.values()) {
    for (const t of needs) union.add(t);
  }
  return union;
}

function scheduleTick() {
  if (_rafId !== null) return;
  _rafId = requestAnimationFrame(() => {
    _rafId = null;
    if (_dirty && _flushFn) {
      _dirty = false;
      _flushFn();
    }
  });
}

// ── Store ────────────────────────────────────────────────────────

interface GameAccum {
  sport: Sport; date: string; awayCode: string; homeCode: string; title: string;
  kalshiAwayTicker: string | null; kalshiHomeTicker: string | null;
  polyTicker: string | null;
  /** Scheduled event start epoch ms captured from `HydraMarket.eventStartTime`
   *  on any frame for this game. Sourced upstream from Polymarket Gamma or
   *  Kalshi milestones. First non-null value wins; later frames don't
   *  overwrite. */
  startTimeMs: number | null;
  /** Set when both outcome tickers in this accum are series-winner markets. */
  series?: { round: number; year: string };
  /** Latest seriesWins observed on any frame keyed to this row's
   *  canonicalSeriesKey: per-team wins keyed by canonical 3-char code. */
  seriesWins?: Record<string, number>;
  /** Latest seriesWinsToClinch observed on any frame for this series.
   *  4 best-of-7, 3 best-of-5, 2 best-of-3. Drives clinch-injection:
   *  inject only when some team's wins ≥ seriesWinsToClinch − 1
   *  (a team is on match point). */
  seriesWinsToClinch?: number;
  /** Set on per-day rows that borrowed series tickers because Kalshi
   *  dropped the per-day market for a possible clincher. Drives the UI
   *  badge / tooltip / trade-modal callout. */
  kalshiViaSeries?: { round: number; year: string };
}

interface MarketState {
  version: number;       // Bumped on RAF tick — triggers re-render
  connected: boolean;
  marketsCount: number;
  orderbooksCount: number;
  /** Date.now() at the most recent ingest across any source/ticker. 0
   *  until the first message arrives. Drives the feed-freshness
   *  indicator and the click-triggered resnapshot heuristic. Updated
   *  on the same RAF flush as `version`, so consumers re-render at
   *  display refresh rate, not per-message. */
  lastMessageMs: number;
  /** Latest hydra backpressure level for the WS connection (0–3). At
   *  >= 2 hydra suspends orderbook frames entirely (market + consensus
   *  keep flowing, coalesced), so orderbook-driven views are rendering
   *  frozen books with no other error signal — surface a stale
   *  indicator. Cleared automatically when orderbook frames resume
   *  (the recovery signal) or the connection is rebuilt. */
  backpressureLevel: number;

  ingestMarket: (market: HydraMarket) => void;
  ingestOrderbook: (ticker: string, book: HydraOrderbook) => void;
  /** Ingest one Unabated consensus update. Stored per
   *  (canonicalGameKey, periodTypeId) so in-period values (Q1 / inning /
   *  set) don't clobber the Full Game (periodTypeId=1) read consumers
   *  show by default. */
  ingestConsensus: (consensus: HydraConsensus) => void;
  /** Replace the active-sport set from hydra's `activeSports` meta frame
   *  (uppercase wire codes; normalized + filtered to g1's registry here). */
  ingestActiveSports: (sports: string[]) => void;
  setConnected: (connected: boolean) => void;
  setBackpressureLevel: (level: number) => void;
  /** Wire the active HydraClient. Called by `useHydra` on mount/unmount. */
  setClient: (client: HydraClient | null) => void;
  /** Declare the set of tickers that currently-mounted views need orderbook
   *  data for. Forwards to the active HydraClient, which narrows the
   *  upstream orderbooks-channel subscription. Views that don't render
   *  depth data never need to call this. */
  setOrderbookNeeds: (viewId: string, tickers: string[]) => void;
  /** Returns the count of tracked tickers that received any frame in
   *  the last `maxAgeMs`, alongside the total tracked count. Drives
   *  the freshness indicator's "is the system actually alive" check —
   *  a single chatty ticker keeping `lastMessageMs` warm shouldn't be
   *  enough to call the dot green. Read on a 1s tick from
   *  useFeedFreshness so it stays current even when nothing arrives. */
  getActivityCount: (maxAgeMs: number) => { active: number; total: number };
  /** Ask the active client for a fresh markets snapshot when the feed
   *  looks mostly-dead — i.e. fewer than ACTIVE_FRACTION_THRESHOLD of
   *  tracked tickers have received any frame in the last `maxAgeMs`.
   *  Catches the case the old "global lastMessageMs" gate missed: one
   *  chatty ticker (or one chatty venue) keeping the connection's
   *  global timestamp fresh while the rest of the cache rots. No-op on
   *  initial load (lastMessageMs === 0) and when the ticker population
   *  is too small for the fraction to be meaningful. */
  requestResnapshotIfStale: (maxAgeMs: number) => void;
  getGames: (
    sport: Sport,
    marketType: MarketType,
    filters?: { statusFilter?: StatusFilter; dateFilter?: DateFilter; selectedDateIso?: string }
  ) => GameRow[];
  /**
   * One row per canonical game with ALL spread + total line markets grouped
   * together. Same date/status filter semantics as `getGames`. The new
   * Spreads & Totals tab consumes this; future event-detail pages can read
   * the same shape. Series rows are excluded — they live in `getSeries`.
   */
  getGameEvents: (
    sport: Sport,
    filters?: { statusFilter?: StatusFilter; dateFilter?: DateFilter; selectedDateIso?: string },
    /** Scope of play. Default 'full' = full game (every existing caller is
     *  unchanged). Pass '1H' for NBA 1st-half or '1Q'–'4Q' for single-quarter
     *  spread/total lines (quarters are Kalshi-only). */
    period?: Period,
  ) => GameEventRow[];
  /** 3-way period moneyline (NBA/WNBA 1st half, soccer 1H/2H, MLB First 5
   *  Innings) for one game — Home / Away / Tie, with the two team rows paired
   *  to Polymarket's binary 1h-moneyline (Poly null for soccer in Phase 1 and
   *  for MLB F5, which is Kalshi-only) and the Tie Kalshi-only. Null when the
   *  game has no winner markets ingested for that period. */
  getPeriodMoneyline: (gameKey: string, period: '1H' | '2H' | 'F5') => PeriodMoneyline | null;
  /** The both-teams-to-score (BTTS) binary market for a game/period — a single
   *  yes/no per scope. `period` may be 'full' (BTTS has no top section, unlike
   *  spread/total/ML). Kalshi-only in Phase 1 (polymarket always null). Null
   *  when no BTTS market is ingested for that period. */
  getBtts: (gameKey: string, period: Period) => {
    kalshi: VenueBook | null;
    polymarket: VenueBook | null;
  } | null;
  /** Which non-full periods have data for this game, by bet type — drives the
   *  Game Lines period tabs (enable only periods that actually have markets).
   *  Each list is ordered 1H, 2H, 1Q, 2Q, 3Q, 4Q. BTTS additionally includes
   *  'full' (it has no top section). One cheap pass over the game's frames (no
   *  event building). Moneyline is half-only (no quarter winner). */
  getAvailablePeriods: (gameKey: string) => {
    spread: Period[];
    total: Period[];
    moneyline: Period[];
    btts: Period[];
  };
  getSeries: (sport: Sport) => SeriesEvent | null;
  /** N-outcome events (soccer Home/Away/Draw today) grouped by event ticker.
   *  Filters mirror getGames semantics ('today' keeps events whose date ===
   *  selectedDateIso; 'active' drops finals; 'settled' keeps only finals) but
   *  DEFAULT TO UNFILTERED — Live discovery, GameDetail's resolver, and
   *  describePolyOrder all need every date. The dashboard passes its toolbar
   *  filters explicitly. */
  getMultiOutcomeEvents: (
    sport: Sport,
    filters?: { statusFilter?: StatusFilter; dateFilter?: DateFilter; selectedDateIso?: string },
  ) => MultiOutcomeEvent[];
  /** Set of sports that have at least one non-closed market frame ingested.
   *  Used by the dashboard toolbar to auto-hide chips for sports with no
   *  live activity. */
  getActiveSports: () => Set<Sport>;
  /** Last market frame for every ticker in the Hydra snapshot. This is an
   *  intentionally blunt escape hatch for fallback discovery surfaces that
   *  need to show raw markets even when the curated row parser cannot group
   *  them confidently. Returned as shallow copies so callers cannot mutate the
   *  store's live frame objects. */
  getMarketsSnapshot: () => HydraMarket[];
  getFeedLog: (source?: FeedLogSource) => FeedLogEntry[];
  getMarketPrice: (ticker: string) => number | null;
  /** Raw HydraMarket frame for a ticker (last update). Used by
   *  `describeMarket` to read Kalshi's title for the authoritative
   *  spread/total line. Returns null when the ticker isn't ingested. */
  getMarket: (ticker: string) => HydraMarket | null;
  /** Scheduled event start epoch ms resolved via the ticker's
   *  `canonicalGameKey` when available, or directly from the market frame
   *  for sports without a canonical key. Returns null when no upstream
   *  start signal has arrived yet. */
  getEventStartTimeMsByTicker: (ticker: string) => number | null;
  /** Latest Full Game (periodTypeId=1) consensus snapshot for a canonical
   *  game key, or null if hydra hasn't broadcast one yet. The
   *  `useConsensusFor` hook wraps this for reactive component reads. */
  getConsensusFor: (canonicalGameKey: string | null | undefined) => HydraConsensus | null;
  /** Seed the canonical-key → startTime cache from an external source
   *  (typically the Unabated gameStates poll). Fills in entries for
   *  games where Polymarket doesn't have a market — so spreads/totals
   *  on niche-sport games still get an event start for countdowns, GTS
   *  expiry, etc. First-write-wins: existing Poly-sourced entries
   *  aren't overwritten by the poll's seed. */
  seedEventStartTimeMsByKey: (gameKey: string, ms: number) => void;
  /** Last trade price in YES-cents (invert for NO at the call site). */
  getLastTradeYesCents: (ticker: string) => number | null;
  /** Raw orderbook (yes / no level lists) by ticker, or null if not yet ingested. */
  getOrderbookRaw: (ticker: string) => HydraOrderbook | null;
  /** Ms since the last ORDERBOOK frame for this ticker (not market/trade
   *  frames), or null if no book has ever arrived. Drives per-book freshness
   *  indicators that must stay honest when deltas freeze but prices tick. */
  getOrderbookAgeMs: (ticker: string) => number | null;
}

const SERIES_TITLE: Record<string, string> = {
  nba: 'NBA Champion',
  wnba: 'WNBA Champion',
  nhl: 'Stanley Cup',
  mlb: 'World Series',
};

export const useMarketStore = create<MarketState>((set, get) => {
  // Wire the flush function to bump version
  _flushFn = () => {
    set({
      version: get().version + 1,
      marketsCount: _markets.size,
      orderbooksCount: _orderbooks.size,
      lastMessageMs: _globalLastReceiveMs,
    });
  };

  return {
    version: 0,
    connected: false,
    marketsCount: 0,
    orderbooksCount: 0,
    lastMessageMs: 0,
    backpressureLevel: 0,

    ingestMarket: (market) => {
      const now = Date.now();
      _markets.set(market.ticker, market);
      _lastReceiveMs.set(market.ticker, now);
      _globalLastReceiveMs = now;
      // Cache canonicalGameKey → start time. Polymarket Gamma and Kalshi
      // milestone frames can carry `eventStartTime`; siblings sharing the
      // canonical key read it back via `getEventStartTimeMsByTicker`.
      // Update policy is in `updateStartTimeCache` — see its definition for
      // the pre-game / post-start / Unabated-lock rules.
      if (market.canonicalGameKey && market.eventStartTime) {
        const ms = Date.parse(market.eventStartTime);
        if (Number.isFinite(ms)) {
          updateStartTimeCache(
            market.canonicalGameKey,
            ms,
            market.source === 'kalshi' ? 'kalshi' : 'polymarket',
          );
        }
      }
      pushFeedLog({
        ts: Date.now(),
        ticker: market.ticker,
        source: market.source,
        sport: market.sport || '?',
        type: 'market',
        yesBid: market.yesBid,
        yesAsk: market.yesAsk,
        volume: market.volume,
      });
      _dirty = true;
      scheduleTick();
    },

    ingestOrderbook: (ticker, book) => {
      const now = Date.now();
      // Captured before the overwrite so the stale-probe below can log
      // ms since the previous frame for this same ticker.
      const prevReceiveMs = _lastReceiveMs.get(ticker);
      _orderbooks.set(ticker, book);
      _lastReceiveMs.set(ticker, now);
      _lastOrderbookMs.set(ticker, now);
      _globalLastReceiveMs = now;

      // Derive price from orderbook (Hydra sends book updates far more often than market updates)
      const bestYesBid = book.yes?.length ? Math.max(...book.yes.map(l => l.price)) : null;
      const bestNoBid = book.no?.length ? Math.max(...book.no.map(l => l.price)) : null;
      const yesAsk = bestNoBid !== null ? 100 - bestNoBid : null;
      const existing = _markets.get(ticker);
      if (existing && (bestYesBid !== null || yesAsk !== null)) {
        existing.yesBid = bestYesBid ?? existing.yesBid;
        existing.yesAsk = yesAsk ?? existing.yesAsk;
        existing.noBid = bestNoBid ?? existing.noBid;
        existing.noAsk = bestYesBid !== null ? 100 - bestYesBid : existing.noAsk;
        existing.lastUpdateMs = Date.now();
      }

      // Stale-probe diagnostic for the "ladder pinned, frames still
      // arriving" failure mode. Off by default — toggle in DevTools
      // with `localStorage.setItem('g1:staleProbe', '1')` then refresh
      // (production builds inline `import.meta.env.DEV = false`, so we
      // need a runtime switch to enable this on the deployed onions).
      // Logs per inbound frame: ticker, canonical game key (so we can
      // tell whether the row's lookup key matches the inbound), per-
      // side level counts AND best-bid prices (counts can churn while
      // top-of-book stays pegged — both signals are needed to tell
      // "data updating" from "data static, only mid-book churning"),
      // and ms since the previous frame for this ticker.
      if (typeof window !== 'undefined' && window.localStorage.getItem('g1:staleProbe') === '1') {
        const since = prevReceiveMs != null ? now - prevReceiveMs : null;
        console.log(
          `[stale-probe] ${ticker} canon=${existing?.canonicalGameKey ?? '?'} Y=${book.yes?.length ?? 0}(${bestYesBid ?? '-'}¢) N=${book.no?.length ?? 0}(${bestNoBid ?? '-'}¢) ${since != null ? `+${since}ms` : 'first'}`,
        );
      }

      // Per-side best-bid depth samples for bookDepthVelocity. Uses the
      // same sanity filter as ladder/helpers.bestBidDepthForSide so the
      // velocity Δ/min in the K Q / P Q columns reads consistently with
      // the displayed K Q depth. Stub bids far above last trade (Kalshi
      // sometimes leaves these dangling) are excluded so a stub being
      // pulled doesn't register as fake draining.
      //
      // CRITICAL: when bestRealBidLevel returns null (best bid drained
      // entirely or only stub bids remain), we still record `0` instead
      // of skipping. The drain-to-zero event is the most actionable
      // sweep signal — skipping the sample leaves the velocity stuck on
      // the last non-zero depth and the cell shows stale data right
      // when the trader needs the alert. Caught by GPT validation
      // 2026-05-25 post-paired-velocity push (a4bd4ff).
      const lastTradeYesCents = existing?.lastPrice ?? null;
      const lastTradeNoCents = lastTradeYesCents != null ? 100 - lastTradeYesCents : null;
      const yesBestReal = bestRealBidLevel(book.yes, lastTradeYesCents);
      const noBestReal = bestRealBidLevel(book.no, lastTradeNoCents);
      recordDepthSample(depthKey(ticker, 'yes'), yesBestReal?.size ?? 0);
      recordDepthSample(depthKey(ticker, 'no'), noBestReal?.size ?? 0);

      pushFeedLog({
        ts: Date.now(),
        ticker,
        source: ticker.startsWith('PM-') ? 'polymarket' : 'kalshi',
        sport: existing?.sport || '?',
        type: 'orderbook',
        yesBid: bestYesBid ?? undefined,
        yesAsk: yesAsk ?? undefined,
        bookLevels: (book.yes?.length ?? 0) + (book.no?.length ?? 0),
      });
      _dirty = true;
      scheduleTick();
    },

    ingestConsensus: (consensus) => {
      // Keyed by (canonicalGameKey, periodTypeId) so in-period values
      // (Q1 / inning / set) coexist with the Full Game read consumers
      // show by default. Skip when the incoming values match cache —
      // hydra dedupes server-side on a ≥1¢ shift, but a redundant
      // re-emit from the snapshot burst on reconnect can still land.
      const key = consensusCacheKey(consensus.canonicalGameKey, consensus.periodTypeId);
      const prev = _consensusByGameKeyAndPeriod.get(key);
      if (
        prev
        && prev.awayFairProbCents === consensus.awayFairProbCents
        && prev.homeFairProbCents === consensus.homeFairProbCents
        && prev.drawFairProbCents === consensus.drawFairProbCents
        && prev.contributingBooks === consensus.contributingBooks
      ) {
        return;
      }
      _consensusByGameKeyAndPeriod.set(key, consensus);
      // Sport tag is sourced off the canonical key prefix (cheaper than
      // walking _markets — every canonical game key starts with sport).
      const sportFromKey = consensus.canonicalGameKey.split('|', 1)[0] ?? '?';
      pushFeedLog({
        ts: Date.now(),
        ticker: consensus.canonicalGameKey,
        source: 'unabated',
        sport: sportFromKey,
        type: 'consensus',
        awayProbCents: consensus.awayFairProbCents,
        homeProbCents: consensus.homeFairProbCents,
        periodTypeId: consensus.periodTypeId,
      });
      _dirty = true;
      scheduleTick();
    },

    ingestActiveSports: (sports) => {
      // Normalize hydra's uppercase wire codes to g1 Sport ids and filter to
      // the known registry — the frame can carry codes g1 doesn't render
      // (NFL is in the live set today); filter, don't error. Each frame is
      // the complete replacement set, never a delta. EXCEPTION: an empty
      // frame while we hold a non-empty set is hydra's post-restart cold
      // cache (~1-2 min window) — keep the last non-empty set; the refill
      // frame replaces it when discovery completes.
      const next = new Set<Sport>();
      for (const code of sports) {
        const norm = SPORT_NORMALIZE[code];
        if (norm) next.add(norm);
      }
      if (next.size === 0 && _activeSports != null && _activeSports.size > 0) return;
      const prev = _activeSports;
      if (prev != null && prev.size === next.size && [...next].every((s) => prev.has(s))) {
        return; // duplicate frame (re-sent on every subscribe op) — no tick
      }
      _activeSports = next;
      _dirty = true;
      scheduleTick();
    },

    setConnected: (connected) => set({ connected }),

    setBackpressureLevel: (level) => set({ backpressureLevel: level }),

    setClient: (client) => {
      _activeClient = client;
      if (client) {
        // Replay any needs that views declared before the client mounted.
        client.setOrderbookNeeds(Array.from(_unionOrderbookNeeds()));
      }
    },

    setOrderbookNeeds: (viewId, tickers) => {
      if (tickers.length === 0) _viewOrderbookNeeds.delete(viewId);
      else _viewOrderbookNeeds.set(viewId, new Set(tickers));
      const active = _unionOrderbookNeeds();
      _activeClient?.setOrderbookNeeds(Array.from(active));
      // Prune bookDepthVelocity history for (ticker, side) keys no
      // longer in the active subscription set so long sessions don't
      // retain stale module-level Maps as the user sweeps across many
      // sport tabs / dates. Each ticker expands to yes+no keys.
      // GPT validation 2026-05-25 caught this as orphaned cleanup.
      const activeKeys = new Set<string>();
      for (const t of active) {
        activeKeys.add(depthKey(t, 'yes'));
        activeKeys.add(depthKey(t, 'no'));
      }
      pruneDepthVelocity(activeKeys);
    },

    requestResnapshotIfStale: (maxAgeMs: number) => {
      const client = _activeClient;
      if (!client) return;
      if (client.lastMessageMs === 0) return; // initial load — let the connect snapshot carry it
      const total = _markets.size;
      if (total < ACTIVE_FRACTION_MIN_POP) return; // not enough population for the fraction to mean anything
      const cutoff = Date.now() - maxAgeMs;
      let active = 0;
      for (const ticker of _markets.keys()) {
        const ms = _lastReceiveMs.get(ticker);
        if (ms !== undefined && ms > cutoff) active++;
      }
      if (active / total < ACTIVE_FRACTION_THRESHOLD) {
        client.requestResnapshot();
      }
    },

    getActivityCount: (maxAgeMs: number) => {
      const cutoff = Date.now() - maxAgeMs;
      let active = 0;
      for (const ticker of _markets.keys()) {
        const ms = _lastReceiveMs.get(ticker);
        if (ms !== undefined && ms > cutoff) active++;
      }
      return { active, total: _markets.size };
    },

    getGames: (sport: Sport, marketType: MarketType = 'moneyline', filters): GameRow[] => {
      // Force dependency on version so this re-derives on tick
      void get().version;

      // Multi-outcome sports (soccer 3-way) don't fit the binary away/home
      // GameRow — they're served by getMultiOutcomeEvents instead.
      if (MULTI_OUTCOME_SPORTS.has(sport)) return [];

      const statusFilter = filters?.statusFilter ?? 'active';
      const dateFilter = filters?.dateFilter ?? 'today';
      const selectedDateIso = filters?.selectedDateIso;

      // Pass 1: Group by game. Date filter applies at parse-time (cheap); status
      // filter defers to Pass 3 since we need to see all markets for the game
      // before deciding if any are still open.
      const games = new Map<string, GameAccum>();

      // Tennis tour routing fallback. Hydra now resolves ITF gender on its side
      // and emits sport "ATP"/"WTA" on the Polymarket tennis frame (only
      // "Tennis" when genuinely unknown), so the per-market block below routes
      // Poly tennis by m.sport. This map is the fallback for a frame hydra still
      // tags "Tennis": borrow the tour from the paired Kalshi side (which knows
      // it via m.sport) by canonicalGameKey. Built only for the tennis tabs.
      const tennisTourByKey = new Map<string, Sport>();
      if (sport === 'atp' || sport === 'wta') {
        for (const [, m] of _markets) {
          if (m.source !== 'kalshi' || !m.canonicalGameKey?.startsWith('tennis|') || !m.sport) continue;
          const tour = SPORT_NORMALIZE[m.sport];
          if (tour === 'atp' || tour === 'wta') tennisTourByKey.set(m.canonicalGameKey, tour);
        }
      }

      for (const [, market] of _markets) {
        // Series-keyed path. Hydra tags every series-related frame
        // (Kalshi KX*SERIES*, KX*SERIESSCORE*, KX*SERIESROADWIN*; Polymarket
        // *-playoffs-who-will-win-series-* slugs) with canonicalSeriesKey =
        // "{sport}|series|{YY}|TEAM_A-TEAM_B" (alpha-sorted, normalized).
        // We collapse cross-venue series tickers onto one row by that key,
        // matching the canonicalGameKey pattern used for per-day games.
        // Round number isn't in the key (Polymarket slug doesn't carry it);
        // we backfill from the Kalshi winner ticker's R{N} segment when
        // present. Only the SERIES winner family populates the row's pricing
        // pills today; SCORE / ROADWIN families share the key but their
        // book contributions are out of scope for this row.
        if (market.canonicalSeriesKey) {
          if (marketType !== 'moneyline') continue;
          const cskParts = market.canonicalSeriesKey.split('|');
          if (cskParts.length !== 4 || cskParts[1] !== 'series') continue;
          const cskSport = SPORT_NORMALIZE[cskParts[0]];
          if (cskSport !== sport) continue;
          const cskYear = cskParts[2];
          const cskTeams = cskParts[3].split('-');
          if (cskTeams.length !== 2) continue;
          const key = market.canonicalSeriesKey;
          if (!games.has(key)) {
            games.set(key, {
              sport, date: `9999-9-${cskYear}`,
              awayCode: cskTeams[0], homeCode: cskTeams[1],
              title: market.title || '',
              kalshiAwayTicker: null, kalshiHomeTicker: null, polyTicker: null,
              startTimeMs: null,
              series: { round: 0, year: cskYear },
            });
          }
          const g = games.get(key)!;
          // Track the latest seriesWins / seriesWinsToClinch across every
          // frame for this canonical series — including SCORE/ROADWIN/Poly
          // tickers, which share the key but don't drive the row's Y/N pills.
          if (market.seriesWins) {
            g.seriesWins = market.seriesWins;
          }
          if (market.seriesWinsToClinch != null) {
            g.seriesWinsToClinch = market.seriesWinsToClinch;
          }
          if (market.source === 'kalshi') {
            const scoreTicker = parseKalshiSeriesScoreTicker(market.ticker);
            if (scoreTicker) {
              const orderedTeams = orderedSeriesTeamsFromScoreTicker(
                scoreTicker.teamChunk,
                [cskTeams[0], cskTeams[1]],
              );
              if (orderedTeams && (g.awayCode !== orderedTeams[0] || g.homeCode !== orderedTeams[1])) {
                const oldAway = g.kalshiAwayTicker;
                const oldHome = g.kalshiHomeTicker;
                g.awayCode = orderedTeams[0];
                g.homeCode = orderedTeams[1];
                g.kalshiAwayTicker = oldHome;
                g.kalshiHomeTicker = oldAway;
              }
              if (g.series && g.series.round === 0 && scoreTicker.round != null) {
                g.series.round = scoreTicker.round;
                g.date = `9999-${scoreTicker.round}-${cskYear}`;
              }
              const yesTeam = market.canonicalYesTeam ?? scoreTicker.teamCode;
              const winsToClinch = market.seriesWinsToClinch ?? g.seriesWinsToClinch;
              const seriesWins = market.seriesWins ?? g.seriesWins;
              if (
                seriesScoreIsCurrentClincher(
                  scoreTicker,
                  yesTeam,
                  [cskTeams[0], cskTeams[1]],
                  seriesWins,
                  winsToClinch,
                )
              ) {
                if (yesTeam === g.awayCode) g.kalshiAwayTicker = market.ticker;
                else if (yesTeam === g.homeCode) g.kalshiHomeTicker = market.ticker;
              }
            } else {
              const parsed = parseTicker(market);
              if (isDirectKalshiTeamSeriesMarket(market)) {
                // Direct championship/conference futures (KXNBA-26-SAS,
                // KXNBAWEST-26-OKC, KXNHL-26-CAR, ...) do not carry a
                // round in the ticker. Newer Hydra builds canonicalize them
                // only when the event's active outcomes have narrowed to a
                // two-team matchup, so canonicalYesTeam is safe to project
                // directly onto the row.
                if (market.canonicalYesTeam === g.awayCode) g.kalshiAwayTicker = market.ticker;
                else if (market.canonicalYesTeam === g.homeCode) g.kalshiHomeTicker = market.ticker;
              } else if (parsed && parsed.seriesRound != null) {
                // canonicalSeriesKey is alpha-sorted; the Kalshi ticker
                // preserves the actual game's away→home order (team1 in
                // the ticker is the away team). Re-orient the row to the
                // Kalshi order when it differs from the alpha default,
                // swapping any tickers we've already accumulated so they
                // stay attached to the right team.
                if (g.awayCode !== parsed.awayCode || g.homeCode !== parsed.homeCode) {
                  const oldAway = g.kalshiAwayTicker;
                  const oldHome = g.kalshiHomeTicker;
                  g.awayCode = parsed.awayCode;
                  g.homeCode = parsed.homeCode;
                  g.kalshiAwayTicker = oldHome;
                  g.kalshiHomeTicker = oldAway;
                }
                if (parsed.outcomeCode === g.awayCode) g.kalshiAwayTicker = market.ticker;
                else if (parsed.outcomeCode === g.homeCode) g.kalshiHomeTicker = market.ticker;
                if (g.series && g.series.round === 0) {
                  g.series.round = parsed.seriesRound;
                  g.date = `9999-${parsed.seriesRound}-${cskYear}`;
                }
              }
            }
            // ROADWIN families share canonicalSeriesKey but parse either as
            // null or with seriesRound undefined; intentionally skipped here.
          } else {
            g.polyTicker = market.ticker;
          }
          continue;
        }

        const parsed = parseTicker(market);
        if (!parsed || parsed.marketType !== marketType) continue;
        // Tour routing for Polymarket tennis. The PM-itf-* slug is gender-less
        // (parses to atp by default), so prefer hydra's resolved tour from
        // m.sport ("ATP"/"WTA"). When hydra couldn't resolve it (m.sport
        // "Tennis" → not in SPORT_NORMALIZE), fall back to borrowing the tour
        // from the paired Kalshi side via canonicalGameKey. Non-tennis tabs and
        // non-Poly markets fall through to the parsed sport untouched.
        let effSport = parsed.sport;
        if (market.source === 'polymarket' && (sport === 'atp' || sport === 'wta')) {
          const hydraTour = market.sport ? SPORT_NORMALIZE[market.sport] : undefined;
          if (hydraTour === 'atp' || hydraTour === 'wta') {
            effSport = hydraTour;
          } else if (market.canonicalGameKey) {
            const mapped = tennisTourByKey.get(market.canonicalGameKey);
            if (mapped) effSport = mapped;
          }
        }
        if (effSport !== sport) continue;
        // Full-game rows only. Period markets (NBA 1H) carry the full-game
        // canonicalGameKey and would otherwise collide on this accumulator
        // (g.polyTicker is last-write-wins below) — the 0cf7549 regression.
        // They surface via getPeriodMoneyline / getGameEvents(…, '1H') instead.
        // Trust BOTH the ticker-derived period and hydra's market.period so a
        // parser miss on a future period shape still can't leak into full game.
        if ((parsed.period ?? 'full') !== 'full' || (market.period != null && market.period !== 'full')) continue;
        const marketStartMs = parseStartTimeMs(market.eventStartTime);
        const eventDate = formatStartDateLocal(marketStartMs) ?? parsed.date;

        // Per-day games: filter to the selected date.
        if (
          parsed.seriesRound == null
          && dateFilter === 'today'
          && selectedDateIso
          && eventDate !== selectedDateIso
        ) continue;

        // Per-day games prefer canonicalGameKey (cross-venue alias collapse,
        // e.g. Kalshi CHW vs Polymarket CWS); fall back to the parsed key.
        // Series tickers without canonicalSeriesKey (older hydra) fall through
        // here too and key by parsed-shape — Kalshi-only fallback, no Poly
        // merge until canonicalSeriesKey lands.
        const key = parsed.seriesRound != null
          ? `${parsed.sport}|series|${parsed.seriesYear}|R${parsed.seriesRound}|${parsed.awayCode}|${parsed.homeCode}`
          : (market.canonicalGameKey
            ?? `${parsed.sport}|${parsed.date}|${parsed.awayCode}|${parsed.homeCode}`);
        if (!games.has(key)) {
          games.set(key, {
            sport, date: eventDate,
            awayCode: parsed.awayCode, homeCode: parsed.homeCode,
            title: market.title || '',
            kalshiAwayTicker: null, kalshiHomeTicker: null, polyTicker: null,
            startTimeMs: null,
            series: parsed.seriesRound != null && parsed.seriesYear != null
              ? { round: parsed.seriesRound, year: parsed.seriesYear }
              : undefined,
          });
        }
        const g = games.get(key)!;

        // Upgrade accumulator codes off raw Polymarket slug aliases
        // (NYL→NY, POR→PDX, LAS→LV, GSV→GS for WNBA; same family for the
        // other sports) when hydra has canonicalized them. Kalshi-side
        // canonicalAwayCode/canonicalHomeCode is game-day-positional;
        // canonicalTeams is alpha-sorted only — prefer Kalshi when
        // present and fall back to canonicalTeams when the current codes
        // are still slug-raw. The final row-build path already prefers
        // kalshi canonical codes, so this keeps g.awayCode/g.homeCode
        // consistent for downstream consumers (e.g. buildPolyBooks's
        // canonicalYesTeam comparison).
        if (market.canonicalAwayCode && market.canonicalHomeCode) {
          g.awayCode = market.canonicalAwayCode;
          g.homeCode = market.canonicalHomeCode;
        } else if (
          market.canonicalTeams
          && !market.canonicalTeams.includes(g.awayCode)
        ) {
          [g.awayCode, g.homeCode] = market.canonicalTeams;
        }

        if (parsed.source === 'kalshi') {
          if (parsed.side === 'away') g.kalshiAwayTicker = market.ticker;
          else g.kalshiHomeTicker = market.ticker;
        } else {
          g.polyTicker = market.ticker;
        }

        // Event start time comes from hydra's `eventStartTime` (Polymarket
        // Gamma or Kalshi milestones). canonicalGameKey collapses venues
        // onto the same accumulator when present; first non-null value wins.
        if (g.startTimeMs == null && marketStartMs != null) {
          g.startTimeMs = marketStartMs;
          g.date = formatStartDateLocal(marketStartMs) ?? g.date;
        }
      }

      // Pass 1.5: clinch-game injection (additive — series rows untouched).
      // When Kalshi drops the per-day game ticker because the game is a
      // possible series clincher, the per-day row has no Kalshi data while
      // a series row exists for the same teams. Copy the series-winner
      // tickers onto the per-day row so the four pills render against the
      // live series book; `kalshiViaSeries` flags the row for the UI
      // badge + trade-modal callout that clarifies pill clicks settle on
      // the series outcome, not tonight's game. The series row itself
      // continues to render at the bottom of the moneyline list unchanged.
      //
      // Both row keys carry canonical (hydra-normalized) team codes:
      //   canonicalGameKey:   "{sport}|{YYYY-MM-DD}|{TEAM_A}|{TEAM_B}"
      //   canonicalSeriesKey: "{sport}|series|{YY}|{TEAM_A}-{TEAM_B}"
      // Joining on the row's accumulator awayCode/homeCode misses when the
      // per-day row was built from a Polymarket-only slug whose raw alias
      // differs from canonical (NHL: poly "mon" vs canonical "MTL"). Pull
      // the canonical pair from the key itself.
      function canonPairFromKey(rowKey: string): [string, string] | null {
        const parts = rowKey.split('|');
        if (parts.length === 4 && parts[1] === 'series') {
          const teams = parts[3].split('-');
          return teams.length === 2 ? [teams[0], teams[1]] : null;
        }
        if (parts.length === 4) {
          return [parts[2], parts[3]];
        }
        return null;
      }
      const seriesByTeams = new Map<string, GameAccum>();
      for (const [key, g] of games) {
        if (!g.series) continue;
        const pair = canonPairFromKey(key);
        if (!pair) continue;
        const k = `${g.sport}|${[pair[0], pair[1]].sort().join('-')}`;
        seriesByTeams.set(k, g);
      }
      for (const [key, g] of games) {
        if (g.series) continue;
        if (g.kalshiAwayTicker || g.kalshiHomeTicker) continue;
        const pair = canonPairFromKey(key);
        if (!pair) continue;
        const k = `${g.sport}|${[pair[0], pair[1]].sort().join('-')}`;
        const series = seriesByTeams.get(k);
        if (!series || !series.series) continue;
        if (!series.kalshiAwayTicker && !series.kalshiHomeTicker) continue;
        // Inject only when some team is on match point (wins ≥ winsToClinch
        // − 1). Otherwise — series not started, or both teams trailing the
        // clinch line — a missing per-day Kalshi ticker is "Kalshi hasn't
        // listed yet" rather than "merged into the series winner". Covers
        // the Game-4-resets-to-3-2-or-2-2 case where a previous on-the-hill
        // game was lost and the next per-day ticker is just a regular game.
        // seriesWins undefined (pre-deploy / Polymarket-only) → skip.
        if (!series.seriesWins) continue;
        const winsToClinch = series.seriesWinsToClinch ?? 4;
        const onMatchPoint = Object.values(series.seriesWins).some(
          (w) => w >= winsToClinch - 1,
        );
        if (!onMatchPoint) continue;
        // Inherit Kalshi-canonical away/home order + tickers from the series
        // row. This both fixes the team alias display ("MON" → "MTL" →
        // "Montreal Canadiens") and lines up the per-team Y/N pills with
        // the right series ticker. Hydra's canonicalGameKey normalization
        // is the same registry the series parser uses, so codes agree.
        g.kalshiAwayTicker = series.kalshiAwayTicker;
        g.kalshiHomeTicker = series.kalshiHomeTicker;
        g.awayCode = series.awayCode;
        g.homeCode = series.homeCode;
        g.kalshiViaSeries = { round: series.series.round, year: series.series.year };
      }

      // Pass 2: Build rows
      const rows: GameRow[] = [];
      const nowMs = Date.now();

      for (const [key, g] of games) {
        const kAwayMkt = (g.kalshiAwayTicker ? _markets.get(g.kalshiAwayTicker) : null) ?? null;
        const kHomeMkt = (g.kalshiHomeTicker ? _markets.get(g.kalshiHomeTicker) : null) ?? null;
        const kAwayBook = (g.kalshiAwayTicker ? _orderbooks.get(g.kalshiAwayTicker) : null) ?? null;
        const kHomeBook = (g.kalshiHomeTicker ? _orderbooks.get(g.kalshiHomeTicker) : null) ?? null;
        const polyMkt = (g.polyTicker ? _markets.get(g.polyTicker) : null) ?? null;
        const polyBook = (g.polyTicker ? _orderbooks.get(g.polyTicker) : null) ?? null;

        function buildPolyBooks(): { away: VenueBook; home: VenueBook } | null {
          if (!polyMkt) return null;
          // YES = the slug's YES team (canonicalYesTeam, else default YES=away,
          // correct for per-day games). The other team gets the inverted book.
          // Shared with getPeriodMoneyline via buildPolyMoneylinePair.
          return buildPolyMoneylinePair(polyMkt, polyBook, g.awayCode);
        }

        // Per-row state derived from the union of source statuses. `gameState`
        // drives both the active/settled filter (series rows use a broader
        // "any non-terminal = live" rule because their sub-tickers ship
        // 'active'/'initialized' between rounds) and the row sort + UI
        // treatment downstream. Computed once per row so callers don't
        // re-derive it per render.
        const statuses = [kAwayMkt?.status, kHomeMkt?.status, polyMkt?.status].filter(Boolean) as string[];
        let gameState = deriveGameState(statuses, !!g.series, g.startTimeMs, nowMs, g.sport);
        // Price-collapse fallback. When deriveGameState returns 'live' but
        // both venues' last prices have converged to 99¢ / 1¢, the market
        // has effectively resolved even if Kalshi staff haven't formally
        // settled yet AND startTimeMs is unknown (no upstream schedule
        // signal on the relevant frames). Required: BOTH Kalshi
        // sides collapsed (one near 99, one near 1) so we don't false-fire
        // on late-game blowouts where Kalshi might briefly hit 97/3 and
        // recover. Polymarket alone counts too — Poly auto-resolves and
        // its 99/1 convergence is essentially never wrong.
        if (gameState === 'live' && !g.series) {
          const isHigh = (p: number | null | undefined) => p != null && p >= 99;
          const isLow = (p: number | null | undefined) => p != null && p <= 1;
          const kaLast = kAwayMkt?.lastPrice;
          const khLast = kHomeMkt?.lastPrice;
          const pLast = polyMkt?.lastPrice;
          const kalshiPaired = (isHigh(kaLast) && isLow(khLast)) || (isLow(kaLast) && isHigh(khLast));
          const polySettled = isHigh(pLast) || isLow(pLast);
          if (kalshiPaired || polySettled) {
            gameState = 'final';
          }
        }

        if (statusFilter !== 'all' && statuses.length > 0) {
          if (g.series) {
            // Series rows: any non-terminal status keeps the row live.
            const anyLive = statuses.some((s) => !TERMINAL_STATUSES.has(s));
            if (statusFilter === 'active' && !anyLive) continue;
            if (statusFilter === 'settled' && anyLive) continue;
          } else {
            if (statusFilter === 'active' && gameState === 'final') continue;
            if (statusFilter === 'settled' && gameState !== 'final') continue;
          }
        }

        // Prefer Kalshi's canonical codes for display (Poly leaves them
        // undefined because its slug order is YES-outcome, not game-day).
        const awayCode = kAwayMkt?.canonicalAwayCode ?? kHomeMkt?.canonicalAwayCode ?? g.awayCode;
        const homeCode = kAwayMkt?.canonicalHomeCode ?? kHomeMkt?.canonicalHomeCode ?? g.homeCode;

        // Winner derivation only matters for FINAL rows; skip the work
        // otherwise. yesIsAway for Poly mirrors buildPolyBooks's logic so
        // the winner attribution stays consistent with how prices render.
        const yesIsAwayForPoly = polyMkt?.canonicalYesTeam == null
          ? true
          : polyMkt.canonicalYesTeam === g.awayCode;
        const finalWinnerSide = gameState === 'final'
          ? deriveFinalWinnerSide(kAwayMkt, kHomeMkt, polyMkt, yesIsAwayForPoly)
          : null;

        rows.push({
          key, sport: g.sport, date: g.date,
          timePt: formatStartTimeLocal(g.startTimeMs),
          startTimeMs: g.startTimeMs,
          gameState,
          finalWinnerSide,
          awayCode, homeCode,
          // Registry name when the sport has one (NBA → "Boston Celtics");
          // else the market's subtitle (Kalshi's yes_sub_title carries the
          // real competitor name for tennis/UFC/boxing/etc., e.g. "Adolfo
          // Daniel Vallejo") so individual sports don't render as bare codes.
          awayName: getTeamDisplayName(awayCode, g.sport) !== awayCode
            ? getTeamDisplayName(awayCode, g.sport)
            : (kAwayMkt?.subtitle ?? awayCode),
          homeName: getTeamDisplayName(homeCode, g.sport) !== homeCode
            ? getTeamDisplayName(homeCode, g.sport)
            : (kHomeMkt?.subtitle ?? homeCode),
          series: g.series,
          kalshiViaSeries: g.kalshiViaSeries,
          kalshi: (kAwayMkt || kHomeMkt) ? { away: buildVenueBookFromMarket(kAwayMkt, kAwayBook), home: buildVenueBookFromMarket(kHomeMkt, kHomeBook) } : null,
          // Series and per-day rows both build a Poly venue book when a
          // matched Poly ticker is in the row. Series rows merge via
          // canonicalSeriesKey (set in Pass 1 above). Note: buildPolyBooks
          // assumes the slug's YES outcome is the row's away team — true
          // for per-day games (slug ordered away→home). For series, slug
          // YES-team orientation isn't guaranteed to match the alpha-sorted
          // canonicalSeriesKey teams; team mapping may render reversed on
          // some series rows. Follow-up: have hydra emit canonicalAwayCode
          // on Poly series slugs (= YES team) and conditionally flip here.
          polymarket: buildPolyBooks(),
        });
      }

      // Sort tiers: FINAL games sink to the bottom of the day's list;
      // within each gameState bucket, chronological by date then event
      // start time (unknown startTimeMs sentinels to +Infinity so it sorts
      // after any known time within the date), with awayCode as the alpha
      // tiebreak.
      rows.sort((a, b) => {
        const aFinal = a.gameState === 'final' ? 1 : 0;
        const bFinal = b.gameState === 'final' ? 1 : 0;
        if (aFinal !== bFinal) return aFinal - bFinal;
        const dateCmp = a.date.localeCompare(b.date);
        if (dateCmp !== 0) return dateCmp;
        const aMs = a.startTimeMs ?? Number.POSITIVE_INFINITY;
        const bMs = b.startTimeMs ?? Number.POSITIVE_INFINITY;
        if (aMs !== bMs) return aMs - bMs;
        return a.awayCode.localeCompare(b.awayCode);
      });
      return rows;
    },

    getActiveSports: (): Set<Sport> => {
      void get().version;
      // Sourced from hydra's `activeSports` meta frame (ingestActiveSports),
      // NOT derived from ingested market frames — the feed is narrowed by
      // the sport filter, so the local market map can't see other sports.
      // (Until 2026-06-11 this walked the whole _markets map, which is why
      // every connect needed a broad discovery snapshot.) Returns a copy:
      // callers union in their own selections.
      return new Set(_activeSports ?? []);
    },

    getMarketsSnapshot: (): HydraMarket[] => {
      void get().version;
      return Array.from(_markets.values(), (market) => ({ ...market }));
    },

    getSeries: (sport: Sport): SeriesEvent | null => {
      void get().version;
      const outcomes: SeriesOutcome[] = [];
      let year: string | null = null;
      let seriesTicker: string | null = null;

      for (const [, market] of _markets) {
        const parsed = parseTicker(market);
        if (!parsed || parsed.sport !== sport || parsed.marketType !== 'series') continue;
        year ??= parsed.seriesYear ?? null;
        // Derive KX{SPORT} prefix from the ticker itself (everything before first dash)
        seriesTicker ??= market.ticker.split('-')[0];

        const book = _orderbooks.get(market.ticker) ?? null;
        const price = market.yesBid != null && market.yesAsk != null
          ? Math.round((market.yesBid + market.yesAsk) / 2) : market.lastPrice ?? null;
        const bids = book?.yes ?? [];
        const asks = book?.no ?? [];
        const bidLiq = bids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
        const askLiq = asks.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
        const liq = bidLiq + askLiq;
        const bookDepth = bids.reduce((s, l) => s + l.size, 0) + asks.reduce((s, l) => s + l.size, 0);
        const vol = market.volume ?? bookDepth;

        outcomes.push({
          ticker: market.ticker,
          code: parsed.outcomeCode,
          name: getTeamDisplayName(parsed.outcomeCode, sport),
          kalshi: {
            marketTicker: market.ticker,
            priceCents: price,
            lastPriceCents: market.lastPrice ?? null,
            volume: vol, liquidity: Math.round(liq),
            yes: toBookLevels(bids), no: toBookLevels(asks),
          },
        });
      }

      if (outcomes.length === 0 || !year || !seriesTicker) return null;

      // Sort by priceCents desc (favorites first), unpriced last
      outcomes.sort((a, b) => {
        const pa = a.kalshi?.priceCents ?? -1;
        const pb = b.kalshi?.priceCents ?? -1;
        return pb - pa;
      });

      return {
        key: `${sport}|${year}`,
        sport,
        year,
        seriesTicker,
        title: `${SERIES_TITLE[sport] ?? sport.toUpperCase()} 20${year}`,
        outcomes,
      };
    },

    getMultiOutcomeEvents: (sport, filters): MultiOutcomeEvent[] => {
      void get().version;
      const nowMs = Date.now();
      // Unfiltered by default — see the interface jsdoc.
      const statusFilter = filters?.statusFilter ?? 'all';
      const dateFilter = filters?.dateFilter ?? 'all';
      const selectedDateIso = filters?.selectedDateIso;

      interface Accum {
        date: string | null;
        startTimeMs: number | null;
        statuses: Set<string>;
        title: string;
        awayCode: string;
        homeCode: string;
        nameHints: Map<string, string>;
        outcomes: Map<string, SeriesOutcome>;
      }
      const byEvent = new Map<string, Accum>();

      for (const [, market] of _markets) {
        const parsed = parseTicker(market);
        const effSport = sportFromMarket(market, parsed);
        if (effSport !== sport) continue;
        if (parsed && parsed.marketType !== 'moneyline') continue;
        // Full-game only. Ticker parse is authoritative (soccer KXWC1H/KXWC2H ⇒
        // '1H'/'2H'); hydra's market.period is the fallback. Either non-full
        // excludes the period moneyline so it never pollutes the full-game 3-way.
        const mktPeriod =
          parsed && parsed.period !== 'full' ? parsed.period : market.period ?? 'full';
        if (mktPeriod !== 'full') continue;

        const marketStartMs = parseStartTimeMs(market.eventStartTime);
        const keyParts = codesFromCanonicalGameKey(market.canonicalGameKey);
        const eventDate = formatStartDateLocal(marketStartMs) ?? keyParts?.date ?? parsed?.date ?? null;

        const eventKey = market.canonicalGameKey || market.eventTicker || market.ticker.replace(/-[^-]+$/, '');
        let acc = byEvent.get(eventKey);
        if (!acc) {
          acc = {
            date: eventDate,
            startTimeMs: null,
            statuses: new Set(),
            title: market.eventTitle || market.title || '',
            awayCode: keyParts?.awayCode ?? parsed?.awayCode ?? '',
            homeCode: keyParts?.homeCode ?? parsed?.homeCode ?? '',
            nameHints: new Map(),
            outcomes: new Map(),
          };
          byEvent.set(eventKey, acc);
        }
        if (market.status) acc.statuses.add(market.status);
        if (acc.startTimeMs == null && marketStartMs != null) {
          acc.startTimeMs = marketStartMs;
          acc.date = formatStartDateLocal(marketStartMs) ?? acc.date;
        }
        if (!acc.title && (market.eventTitle || market.title)) acc.title = market.eventTitle || market.title || '';
        if (market.canonicalAwayCode && market.canonicalHomeCode) {
          acc.awayCode = market.canonicalAwayCode.toUpperCase();
          acc.homeCode = market.canonicalHomeCode.toUpperCase();
        } else if ((!acc.awayCode || !acc.homeCode) && parsed?.awayCode && parsed?.homeCode) {
          acc.awayCode = parsed.awayCode;
          acc.homeCode = parsed.homeCode;
        }

        const code =
          normalizeOutcomeCode(market.yesOutcome) ??
          normalizeOutcomeCode(market.canonicalYesTeam) ??
          normalizeOutcomeCode(parsed?.outcomeCode) ??
          (effSport === 'soccer' && market.source === 'polymarket'
            ? soccerPolyOutcomeFromTicker(market.ticker)
            : null);
        if (!code) continue;
        const isDraw = isDrawOutcome(code);
        if (
          effSport === 'soccer' &&
          (
            !acc.awayCode ||
            !acc.homeCode ||
            (!isDraw && code !== acc.awayCode && code !== acc.homeCode)
          )
        ) {
          continue;
        }
        const book = _orderbooks.get(market.ticker) ?? null;
        const venueBook = buildVenueBookFromMarket(market, book);
        if (market.subtitle && !isDraw) {
          acc.nameHints.set(code, market.subtitle);
        }
        let outcome = acc.outcomes.get(code);
        if (!outcome) {
          const role: SeriesOutcome['role'] =
            isDraw ? 'draw'
              : code === acc.awayCode ? 'away'
                : code === acc.homeCode ? 'home'
                  : 'other';
          outcome = {
            ticker: market.ticker,
            code,
            name: isDraw
              ? 'Draw'
              : (acc.nameHints.get(code) ?? getTeamDisplayName(code, sport) ?? code),
            role,
            kalshi: null,
            polymarket: null,
          };
          acc.outcomes.set(code, outcome);
        }
        if (market.source === 'kalshi') {
          outcome.kalshi = venueBook;
          outcome.ticker = venueBook.marketTicker || outcome.ticker;
        } else {
          outcome.polymarket = venueBook;
          if (!outcome.kalshi) outcome.ticker = venueBook.marketTicker || outcome.ticker;
        }
        if (!isDraw && (outcome.name === code || !outcome.name)) {
          outcome.name = acc.nameHints.get(code) ?? getTeamDisplayName(code, sport) ?? code;
        }
      }

      const out: MultiOutcomeEvent[] = [];
      for (const [key, acc] of byEvent) {
        if (acc.outcomes.size === 0) continue;
        const outcomes = Array.from(acc.outcomes.values());
        const roleRank = (o: SeriesOutcome): number =>
          o.role === 'home' ? 0
            : o.role === 'away' ? 1
              : o.role === 'draw' ? 2
                : 3;
        outcomes.sort((a, b) => {
          const rank = roleRank(a) - roleRank(b);
          if (rank !== 0) return rank;
          const pa = a.kalshi?.priceCents ?? a.polymarket?.priceCents ?? -1;
          const pb = b.kalshi?.priceCents ?? b.polymarket?.priceCents ?? -1;
          return pb - pa;
        });
        const awayName = acc.awayCode ? getTeamDisplayName(acc.awayCode, sport) : '';
        const homeName = acc.homeCode ? getTeamDisplayName(acc.homeCode, sport) : '';
        const title =
          acc.awayCode && acc.homeCode
            ? `${awayName || acc.awayCode} @ ${homeName || acc.homeCode}`
            : acc.title;
        out.push({
          key,
          sport,
          category: `${sport}-game`,
          date: acc.date,
          timePt: formatStartTimeLocal(acc.startTimeMs),
          startTimeMs: acc.startTimeMs,
          gameState: deriveGameState([...acc.statuses], false, acc.startTimeMs, nowMs, sport),
          title,
          awayCode: acc.awayCode,
          homeCode: acc.homeCode,
          awayName: awayName || acc.awayCode,
          homeName: homeName || acc.homeCode,
          outcomes,
        });
      }

      // Toolbar filters — same semantics as getGames. Applied to the
      // assembled events (the list is small) rather than per-market, since
      // an event's date refines as its markets accumulate.
      const filtered = out.filter((e) => {
        if (dateFilter === 'today' && selectedDateIso && e.date !== selectedDateIso) return false;
        if (statusFilter === 'active' && e.gameState === 'final') return false;
        if (statusFilter === 'settled' && e.gameState !== 'final') return false;
        return true;
      });

      const stateRank = { live: 0, pre: 1, final: 2 } as const;
      filtered.sort((a, b) => {
        if (stateRank[a.gameState] !== stateRank[b.gameState]) return stateRank[a.gameState] - stateRank[b.gameState];
        return (a.startTimeMs ?? Infinity) - (b.startTimeMs ?? Infinity);
      });
      return filtered;
    },

    getGameEvents: (sport, filters, period = 'full'): GameEventRow[] => {
      void get().version;

      const statusFilter = filters?.statusFilter ?? 'active';
      const dateFilter = filters?.dateFilter ?? 'today';
      const selectedDateIso = filters?.selectedDateIso;

      // Pass 1 — group spread/total tickers by canonical event key.
      // Key: hydra's canonicalGameKey when present (preferred — collapses
      // cross-venue slug aliases like CHW/CWS), else "sport|date|A|B" with
      // alpha-sorted teams to match hydra's convention.
      interface EventAccum {
        sport: Sport;
        date: string;
        /** Display codes — preferred from hydra's canonical (e.g. "ARI"
         *  when Kalshi raw says "AZ"); falls back to raw when canonical
         *  isn't provided. Used for getTeamDisplayName + chip/panel UI. */
        awayCode: string;
        homeCode: string;
        /** Raw codes exactly as they appear in Kalshi ticker outcomes —
         *  what `sp.teamCode` is compared against to determine favored.
         *  Must NOT be canonicalised or the AZ-vs-ARI mismatch flips
         *  every underdog spread bucket to "home". */
        rawAwayCode: string;
        rawHomeCode: string;
        /** Scheduled event start epoch ms — first non-null eventStartTime
         *  seen on any sub-ticker for this canonical event. Wins over later
         *  frames so we don't churn it on every update. Null when unknown. */
        startTimeMs: number | null;
        /** All statuses seen across this event's sub-tickers (Kalshi spreads,
         *  totals, Poly spreads, totals). Drives gameState derivation in
         *  Pass 2 — same rule as moneyline rows. */
        statuses: Set<string>;
        kalshiSpreads: Map<string, { ticker: string; teamCode: string; bucket: number }>;
        kalshiTotals: Map<string, { ticker: string; strike: number }>;
        polySpreads: Array<{ ticker: string; line: number; side: 'home' | 'away' }>;
        polyTotals: Array<{ ticker: string; line: number }>;
      }
      const events = new Map<string, EventAccum>();

      // Cross-key index: eventStartTime + statuses pulled off ALL markets
      // sharing a canonicalGameKey, regardless of marketType. Hydra's
      // eventStartTime often lives on the Gamma per-game
      // record, which most reliably attaches to the per-day moneyline
      // slug (`PM-{sport}-{away}-{home}-{date}`) — not necessarily the
      // per-line spread/total slugs. Without this, getGameEvents misses
      // start time + the GAME ticker's status flips, so spread/total
      // events never light up live or sort by start time. We walk
      // _markets once here and have Pass 2 fall back to the index when
      // the spread/total accumulator is empty.
      const startTimeByKey = new Map<string, number>();
      const startDateByKey = new Map<string, string>();
      const statusesByKey = new Map<string, Set<string>>();
      for (const [, m] of _markets) {
        const ck = m.canonicalGameKey;
        if (!ck) continue;
        const ms = parseStartTimeMs(m.eventStartTime);
        if (ms != null && !startTimeByKey.has(ck)) {
          startTimeByKey.set(ck, ms);
          const localDate = formatStartDateLocal(ms);
          if (localDate) startDateByKey.set(ck, localDate);
        }
        if (m.status) {
          let set = statusesByKey.get(ck);
          if (!set) { set = new Set(); statusesByKey.set(ck, set); }
          set.add(m.status);
        }
      }

      for (const [, market] of _markets) {
        const parsed = parseTicker(market);
        if (!parsed) continue;
        if (parsed.sport !== sport) continue;
        // Scope to the requested period. Default 'full' excludes NBA period
        // lines (1H + quarters, which share the full-game canonicalGameKey)
        // from the full-game event rows — the 0cf7549 invariant. A non-full
        // period returns only its own lines. The ticker parse is authoritative
        // for the period token (KXNBA1QTOTAL ⇒ '1Q'); hydra's market.period is
        // the fallback signal. Whichever is non-full wins, so a period market
        // can never be misclassified 'full' and leak into the full-game rows.
        const mktPeriod: Period =
          parsed.period !== 'full'
            ? parsed.period
            : market.period && market.period !== 'full'
              ? (market.period as Period)
              : 'full';
        if (mktPeriod !== period) continue;
        if (parsed.marketType !== 'spread' && parsed.marketType !== 'total') continue;
        const indexedStart = market.canonicalGameKey ? startTimeByKey.get(market.canonicalGameKey) : undefined;
        const marketStartMs = parseStartTimeMs(market.eventStartTime) ?? indexedStart ?? null;
        const eventDate = formatStartDateLocal(marketStartMs) ?? (
          market.canonicalGameKey ? startDateByKey.get(market.canonicalGameKey) : undefined
        ) ?? parsed.date;
        if (
          dateFilter === 'today' &&
          selectedDateIso &&
          eventDate !== selectedDateIso
        ) continue;

        const sortedTeams = [parsed.awayCode, parsed.homeCode].sort();
        const fallbackKey = `${parsed.sport}|${eventDate}|${sortedTeams[0]}|${sortedTeams[1]}`;
        const key = market.canonicalGameKey ?? fallbackKey;

        let e = events.get(key);
        if (!e) {
          e = {
            sport: parsed.sport,
            date: eventDate,
            awayCode: parsed.awayCode,
            homeCode: parsed.homeCode,
            rawAwayCode: parsed.awayCode,
            rawHomeCode: parsed.homeCode,
            startTimeMs: null,
            statuses: new Set(),
            kalshiSpreads: new Map(),
            kalshiTotals: new Map(),
            polySpreads: [],
            polyTotals: [],
          };
          events.set(key, e);
        }

        // Display codes upgrade to hydra's canonical (e.g. "AZ" → "ARI")
        // when provided; raw codes stay pinned to the Kalshi ticker
        // outcome so the favored comparison below still matches.
        if (market.canonicalAwayCode && market.canonicalHomeCode) {
          e.awayCode = market.canonicalAwayCode;
          e.homeCode = market.canonicalHomeCode;
        }
        // If this event was first seeded by a Polymarket slug alias
        // (e.g. OAK) and a Kalshi frame arrives later with a different raw
        // code (e.g. SAC), refresh the raw comparison codes from Kalshi.
        // `favored` below compares Kalshi spread outcome prefixes against
        // these fields; using Poly aliases there flips the side.
        //
        // CRITICAL: only trust moneyline / spread tickers for this. Totals
        // tickers don't encode a team in their outcome ("…-2") so the
        // parser falls back to a naive mid-split of the matchup string,
        // which silently breaks for variable-length codes (e.g. TBNYY
        // mid-splits to TBN/YY). Letting a total overwrite the rawAway/
        // HomeCode then poisons the spread-favored comparison so TB
        // tickers get classified as home-favored and collide with NYY
        // entries under my dedupe. Gating the write to moneyline/spread
        // — both of which anchor on a real teamCandidate from the
        // outcome — keeps the raw codes pinned to truth.
        // (This loop already filters to spread/total markets — moneyline
        // tickers never reach here. So gating on marketType === 'spread'
        // alone is sufficient to keep totals' bad mid-split codes out.)
        if (parsed.source === 'kalshi' && parsed.marketType === 'spread') {
          e.rawAwayCode = parsed.awayCode;
          e.rawHomeCode = parsed.homeCode;
        }

        // Capture event start time + per-ticker status for live/final
        // derivation. Mirrors moneyline GameAccum.startTimeMs — first
        // non-null wins; later frames don't overwrite. Status flows from
        // every sub-ticker so gameState reflects the union.
        if (e.startTimeMs == null && marketStartMs != null) {
          e.startTimeMs = marketStartMs;
          e.date = formatStartDateLocal(marketStartMs) ?? e.date;
        }
        if (market.status) e.statuses.add(market.status);

        if (parsed.source === 'kalshi') {
          if (
            parsed.marketType === 'spread' &&
            parsed.spreadTeamCode !== undefined &&
            parsed.spreadBucket !== undefined
          ) {
            e.kalshiSpreads.set(market.ticker, {
              ticker: market.ticker,
              teamCode: parsed.spreadTeamCode,
              bucket: parsed.spreadBucket,
            });
          } else if (
            parsed.marketType === 'total' &&
            parsed.totalStrike !== undefined
          ) {
            e.kalshiTotals.set(market.ticker, {
              ticker: market.ticker,
              strike: parsed.totalStrike,
            });
          }
        } else {
          if (
            parsed.marketType === 'spread' &&
            parsed.polyLine !== undefined &&
            parsed.polySpreadSide
          ) {
            e.polySpreads.push({
              ticker: market.ticker,
              line: parsed.polyLine,
              side: parsed.polySpreadSide,
            });
          } else if (parsed.marketType === 'total' && parsed.polyLine !== undefined) {
            e.polyTotals.push({ ticker: market.ticker, line: parsed.polyLine });
          }
        }
      }

      // Pass 2 — build SpreadLine[] / TotalLine[] per event.
      // Kalshi sports spread/total markets are observed in both 'open' and
      // 'active' statuses (verified live 2026-05-12: WNBA spread/total all
      // ship as 'active', NBA moneyline ships as 'open'). Treat any
      // non-terminal status as live — same rule the series-row path in
      // `getGames` already uses.
      const TERMINAL_STATUSES = new Set(['closed', 'settled', 'finalized', 'determined']);
      const isMarketActive = (ticker: string): boolean | null => {
        const mkt = _markets.get(ticker);
        if (!mkt?.status) return null;
        return !TERMINAL_STATUSES.has(mkt.status);
      };
      const passesStatus = (ticker: string): boolean => {
        if (statusFilter === 'all') return true;
        const live = isMarketActive(ticker);
        if (live == null) return statusFilter === 'active'; // unknown → keep active
        return statusFilter === 'active' ? live : !live;
      };

      const rows: GameEventRow[] = [];
      const nowMs = Date.now();
      for (const [key, e] of events) {
        const spreads: SpreadLine[] = [];
        for (const sp of e.kalshiSpreads.values()) {
          if (!passesStatus(sp.ticker)) continue;
          const mkt = _markets.get(sp.ticker) ?? null;
          const book = _orderbooks.get(sp.ticker) ?? null;
          // Hydra forwards Kalshi's per-market subtitle (`yes_sub_title`)
          // verbatim — e.g. "Montreal wins by over 2.5 goals" — which is
          // authoritative across all sports. Falls back to `title` for
          // pre-subtitle hydra frames, then to `bucket − 0.5` only when
          // neither is available (legacy frames, parser miss). Per-sport
          // bucket math intentionally avoided: MLB names buckets by min
          // margin, NBA/NHL by line floor.
          const lineMag =
            parseLineFromSubtitle(mkt?.subtitle) ??
            parseLineFromSubtitle(mkt?.title) ??
            sp.bucket - 0.5;
          // Moneyline-equivalent buckets (lineMag < 1) live in the game
          // market — Kalshi UI hides them from the spread tab. Drop them.
          if (lineMag < 1) continue;
          // Compare against raw Kalshi codes — sp.teamCode is the outcome
          // prefix verbatim (e.g. "AZ"), which won't match a canonicalised
          // e.awayCode like "ARI" even though they refer to the same team.
          const favored: 'away' | 'home' =
            sp.teamCode === e.rawAwayCode ? 'away' : 'home';
          const signedLine = favored === 'away' ? -lineMag : +lineMag;
          const kalshi = buildVenueBookFromMarket(mkt, book);
          // Side-aware Poly match: poly emits one slug per side per line
          // (`spread-home-5pt5` vs `spread-away-5pt5`). Match must align on
          // BOTH magnitude AND side — picking the wrong-side slug would
          // attach a poly book that represents the OPPOSITE bet, and the
          // modal would silently route trades to the wrong outcome token.
          const polyMatch = matchPolySpread(e.polySpreads, lineMag, favored);
          const polyMkt = polyMatch ? _markets.get(polyMatch.ticker) ?? null : null;
          const polyBook = polyMatch ? _orderbooks.get(polyMatch.ticker) ?? null : null;
          const polymarket = polyMkt
            ? buildVenueBookFromMarket(polyMkt, polyBook)
            : undefined;
          spreads.push({ line: signedLine, favored, kalshi, polymarket });
        }

        if (period !== 'full') {
          for (const poly of e.polySpreads) {
            if (!passesStatus(poly.ticker)) continue;
            const favored = poly.side;
            const signedLine = favored === 'away' ? -poly.line : +poly.line;
            const alreadySeeded = spreads.some(
              (sp) => Math.abs(Math.abs(sp.line) - poly.line) <= 0.25 && sp.favored === favored,
            );
            if (alreadySeeded) continue;
            const polyMkt = _markets.get(poly.ticker) ?? null;
            const polyBook = _orderbooks.get(poly.ticker) ?? null;
            const polymarket = polyMkt
              ? buildVenueBookFromMarket(polyMkt, polyBook)
              : undefined;
            if (polymarket) spreads.push({ line: signedLine, favored, polymarket });
          }
        }

        // Collision guard: same (magnitude, favored) shouldn't appear
        // twice — that's the same conceptual bet. If two distinct
        // Kalshi tickers end up with the same parsed mag (parser miss
        // + bucket fallback colliding, or stray subtitle drift),
        // collapse them and keep the entry whose Kalshi book is
        // actually live (non-null priceCents). Without this, the chip
        // strip surfaces two visually-identical "+1.5" chips whose
        // underlying markets differ in price, and the trader has no
        // way to tell which one they're picking.
        const byMagFav = new Map<string, SpreadLine>();
        for (const sp of spreads) {
          const key = `${Math.abs(sp.line)}|${sp.favored}`;
          const existing = byMagFav.get(key);
          if (!existing) {
            byMagFav.set(key, sp);
            continue;
          }
          // Prefer the one with a live price; otherwise keep the first.
          const existingLive = (existing.kalshi?.priceCents ?? existing.polymarket?.priceCents ?? null) != null;
          const candidateLive = (sp.kalshi?.priceCents ?? sp.polymarket?.priceCents ?? null) != null;
          if (candidateLive && !existingLive) {
            byMagFav.set(key, sp);
          }
        }
        const deduped = Array.from(byMagFav.values());
        deduped.sort((a, b) => a.line - b.line);
        spreads.length = 0;
        spreads.push(...deduped);

        const totals: TotalLine[] = [];
        for (const t of e.kalshiTotals.values()) {
          if (!passesStatus(t.ticker)) continue;
          const mkt = _markets.get(t.ticker) ?? null;
          const book = _orderbooks.get(t.ticker) ?? null;
          // Hydra forwards Kalshi's per-market subtitle (`yes_sub_title`)
          // for totals — e.g. "Over 203.5 points scored" — which is the
          // canonical line. Per-sport strike math kept as a fallback for
          // pre-subtitle hydra frames only: MLB + WNBA name by minimum
          // total (strike − 0.5); NBA/NHL by line floor (strike + 0.5).
          const subtitleLine = parseLineFromSubtitle(mkt?.subtitle);
          // Min-margin naming: the Kalshi strike is the minimum total for YES,
          // so the binary line is strike − 0.5 (MLB runs, WNBA points, soccer
          // goals — "1 or more goals" ⇒ Over 0.5). NBA/NHL name by line floor
          // (strike + 0.5). Subtitle is authoritative when present; this is the
          // pre-subtitle fallback only.
          const minMarginSport = e.sport === 'mlb' || e.sport === 'wnba' || e.sport === 'soccer';
          const line = subtitleLine ?? (minMarginSport ? t.strike - 0.5 : t.strike + 0.5);
          const kalshi = buildVenueBookFromMarket(mkt, book);
          const polyMatch = e.polyTotals.find(
            (p) => Math.abs(p.line - line) <= 0.25,
          );
          const polyMkt = polyMatch ? _markets.get(polyMatch.ticker) ?? null : null;
          const polyBook = polyMatch ? _orderbooks.get(polyMatch.ticker) ?? null : null;
          const polymarket = polyMkt
            ? buildVenueBookFromMarket(polyMkt, polyBook)
            : undefined;
          totals.push({ line, kalshi, polymarket });
        }

        // Surface Polymarket totals that DON'T line up with any Kalshi strike as
        // their own rows — INCLUDING full-game. The old `period !== 'full'` guard
        // dropped these, so a game whose Poly total sits >0.25 off the Kalshi line
        // (the two venues post slightly different totals) showed a BLANK Polymarket
        // column instead of the real Poly market — the MLB-totals issue a trader
        // reported. The alreadySeeded check still prevents duplicating a Poly book
        // already attached to a Kalshi-anchored row.
        for (const poly of e.polyTotals) {
          if (!passesStatus(poly.ticker)) continue;
          const alreadySeeded = totals.some((t) => Math.abs(t.line - poly.line) <= 0.25);
          if (alreadySeeded) continue;
          const polyMkt = _markets.get(poly.ticker) ?? null;
          const polyBook = _orderbooks.get(poly.ticker) ?? null;
          const polymarket = polyMkt
            ? buildVenueBookFromMarket(polyMkt, polyBook)
            : undefined;
          if (polymarket) totals.push({ line: poly.line, polymarket });
        }
        totals.sort((a, b) => a.line - b.line);

        if (spreads.length === 0 && totals.length === 0) continue;

        // Cross-key fallback: prefer eventStartTime / status from any
        // market sharing this canonicalGameKey when the spread/total
        // accumulator is missing data. The schedule signal often arrives
        // on a per-game moneyline frame rather than per-line slugs, and
        // the GAME ticker's status is the cleanest live/final signal —
        // both are captured by the
        // startTimeByKey / statusesByKey indices above.
        const startTimeMs = e.startTimeMs ?? startTimeByKey.get(key) ?? null;
        const unionStatuses = new Set(e.statuses);
        const xStatuses = statusesByKey.get(key);
        if (xStatuses) for (const s of xStatuses) unionStatuses.add(s);
        const gameState = deriveGameState(
          Array.from(unionStatuses),
          false, // spread/total events aren't series rows — same strict 'open'+startTime live rule
          startTimeMs,
          nowMs,
          e.sport,
        );

        rows.push({
          key,
          sport: e.sport,
          date: e.date,
          timePt: formatStartTimeLocal(startTimeMs),
          startTimeMs,
          gameState,
          awayCode: e.awayCode,
          homeCode: e.homeCode,
          awayName: getTeamDisplayName(e.awayCode, e.sport),
          homeName: getTeamDisplayName(e.homeCode, e.sport),
          spreads,
          totals,
        });
      }

      // Same sort tier as moneyline rows: FINAL events sink to the bottom of
      // the day's list, then chronological by date + event start time, with
      // alpha tiebreak. Unknown startTimeMs sentinels to +Infinity within
      // its date bucket.
      rows.sort((a, b) => {
        const aFinal = a.gameState === 'final' ? 1 : 0;
        const bFinal = b.gameState === 'final' ? 1 : 0;
        if (aFinal !== bFinal) return aFinal - bFinal;
        const dateCmp = a.date.localeCompare(b.date);
        if (dateCmp !== 0) return dateCmp;
        const aMs = a.startTimeMs ?? Number.POSITIVE_INFINITY;
        const bMs = b.startTimeMs ?? Number.POSITIVE_INFINITY;
        if (aMs !== bMs) return aMs - bMs;
        return a.awayCode.localeCompare(b.awayCode);
      });

      return rows;
    },

    getPeriodMoneyline: (gameKey, period): PeriodMoneyline | null => {
      void get().version;
      // Collect the (up to 3) Kalshi winner outcomes + the Poly book(s) for this
      // game's period. Kalshi: KXNBA1HWINNER / KXWC1H / KXWC2H-…-{TEAM|TIE} →
      // away / home / tie books. Polymarket has two shapes:
      //   • NBA/WNBA: ONE binary `-1h-moneyline` → paired native/inverted onto
      //     the two team rows (no tie).
      //   • Soccer: THREE separate markets `-halftime-result-{away|home|draw}`
      //     (parsed to a team code + isTie) → placed individually, incl. a draw.
      // All share the full-game canonicalGameKey.
      let awayKalshi: VenueBook | null = null;
      let homeKalshi: VenueBook | null = null;
      let tieKalshi: VenueBook | null = null;
      let awayCode = '';
      let homeCode = '';
      let nbaPolyMkt: HydraMarket | null = null; // NBA single-binary to pair
      // Soccer: per-outcome Poly books, matched by team code below.
      let awayPolySoccer: VenueBook | null = null;
      let homePolySoccer: VenueBook | null = null;
      let tiePolySoccer: VenueBook | null = null;
      const polySoccer: Array<{ parsed: ParsedTicker; mkt: HydraMarket }> = [];
      for (const [, m] of _markets) {
        if (m.canonicalGameKey !== gameKey) continue;
        const parsed = parseTicker(m);
        if (!parsed || parsed.period !== period || parsed.marketType !== 'moneyline') continue;
        if (m.source === 'kalshi') {
          awayCode = parsed.awayCode;
          homeCode = parsed.homeCode;
          const book = buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null);
          if (parsed.isTie) tieKalshi = book;
          else if (parsed.outcomeCode === parsed.awayCode) awayKalshi = book;
          else if (parsed.outcomeCode === parsed.homeCode) homeKalshi = book;
        } else if (m.source === 'polymarket') {
          if (MULTI_OUTCOME_SPORTS.has(parsed.sport)) {
            // Soccer 3-way — defer placement until codes are known.
            polySoccer.push({ parsed, mkt: m });
            // Poly-only game (Kalshi not open yet): seed the away/home identity
            // from hydra's reconciled canonical team by slot, so the slug's
            // alias spelling (`kr`) doesn't block the match below.
            const canon = polyCanonicalOutcome(m);
            if (canon && canon !== 'DRAW' && !parsed.isTie) {
              if (parsed.side === 'away' && !awayCode) awayCode = canon;
              else if (parsed.side === 'home' && !homeCode) homeCode = canon;
            }
          } else {
            nbaPolyMkt = m; // NBA binary
          }
          // Poly slug lacks team labels until paired; capture codes if Kalshi
          // hasn't (poly-only game, before the Kalshi side opens).
          if (!awayCode) awayCode = parsed.awayCode;
          if (!homeCode) homeCode = parsed.homeCode;
        }
      }
      if (!awayKalshi && !homeKalshi && !tieKalshi && !nbaPolyMkt && polySoccer.length === 0) return null;

      // Soccer: place each Poly outcome by its team code (draw → tie). Prefer
      // hydra's reconciled canonical team (`yesOutcome` = KOR/MEX) over the
      // slug-derived parse so alias countries pair — Poly spells Korea `kr`,
      // which the slug uppercases to `KR` ≠ Kalshi `KOR`. Falls back to the slug
      // parse for frames hydra hasn't reconciled yet. Matching by code (not slug
      // position) stays correct when Poly's away/home order differs from Kalshi's.
      for (const { parsed, mkt } of polySoccer) {
        const book = buildVenueBookFromMarket(mkt, _orderbooks.get(mkt.ticker) ?? null);
        if (parsed.isTie) { tiePolySoccer = book; continue; }
        const code = polyCanonicalOutcome(mkt) ?? parsed.outcomeCode;
        if (code === homeCode) homePolySoccer = book;
        else if (code === awayCode) awayPolySoccer = book;
      }

      // NBA: pair the single binary native/inverted onto the two team rows.
      let awayPoly: VenueBook | null = awayPolySoccer;
      let homePoly: VenueBook | null = homePolySoccer;
      if (nbaPolyMkt) {
        const pair = buildPolyMoneylinePair(nbaPolyMkt, _orderbooks.get(nbaPolyMkt.ticker) ?? null, awayCode);
        awayPoly = pair.away;
        homePoly = pair.home;
      }
      return {
        gameKey, period, awayCode, homeCode,
        away: { kalshi: awayKalshi, polymarket: awayPoly },
        home: { kalshi: homeKalshi, polymarket: homePoly },
        tie: { kalshi: tieKalshi, polymarket: tiePolySoccer },
      };
    },

    getBtts: (gameKey, period) => {
      void get().version;
      // Both-teams-to-score is a single binary per game/period (Kalshi
      // KX*{period}BTTS-…-BTTS). One Kalshi book (+ Poly in a later phase),
      // collapsed onto the full-game canonicalGameKey like every period market.
      let kalshi: VenueBook | null = null;
      let polymarket: VenueBook | null = null;
      for (const [, m] of _markets) {
        if (m.canonicalGameKey !== gameKey) continue;
        const parsed = parseTicker(m);
        if (!parsed || parsed.marketType !== 'btts' || parsed.period !== period) continue;
        const book = buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null);
        if (m.source === 'kalshi') kalshi = book;
        else if (m.source === 'polymarket') polymarket = book;
      }
      if (!kalshi && !polymarket) return null;
      return { kalshi, polymarket };
    },

    getAvailablePeriods: (gameKey: string) => {
      void get().version;
      const spread = new Set<Period>();
      const total = new Set<Period>();
      const moneyline = new Set<Period>();
      const btts = new Set<Period>();
      for (const [, m] of _markets) {
        if (m.canonicalGameKey !== gameKey) continue;
        const parsed = parseTicker(m);
        if (!parsed) continue;
        // Same authoritative-ticker-then-hydra classification as getGameEvents.
        const p: Period =
          parsed.period !== 'full'
            ? parsed.period
            : m.period && m.period !== 'full'
              ? (m.period as Period)
              : 'full';
        if (parsed.marketType === 'btts') {
          // BTTS has no top section, so its Full Game line belongs here too.
          btts.add(p);
          continue;
        }
        // spread/total/moneyline full game live in the top sections — period-only here.
        if (p === 'full') continue;
        if (parsed.marketType === 'spread') spread.add(p);
        else if (parsed.marketType === 'total') total.add(p);
        else if (parsed.marketType === 'moneyline') moneyline.add(p);
      }
      const ORDER: Period[] = ['full', '1H', '2H', 'F5', '1Q', '2Q', '3Q', '4Q'];
      const sortP = (s: Set<Period>) => ORDER.filter((p) => s.has(p));
      return {
        spread: sortP(spread),
        total: sortP(total),
        moneyline: sortP(moneyline),
        btts: sortP(btts),
      };
    },

    getFeedLog: (source?: FeedLogSource): FeedLogEntry[] => {
      void get().version;
      if (source === 'kalshi') return [..._feedLogKalshi].reverse();
      if (source === 'polymarket') return [..._feedLogPoly].reverse();
      if (source === 'unabated') return [..._feedLogUnabated].reverse();
      // All sources merged, newest first
      return [..._feedLogKalshi, ..._feedLogPoly, ..._feedLogUnabated]
        .sort((a, b) => b.ts - a.ts)
        .slice(0, FEED_LOG_MAX);
    },

    getOrderbookRaw: (ticker: string): HydraOrderbook | null => {
      void get().version; // re-derive on book updates
      return _orderbooks.get(ticker) ?? null;
    },

    getOrderbookAgeMs: (ticker: string): number | null => {
      const ms = _lastOrderbookMs.get(ticker);
      return ms === undefined ? null : Date.now() - ms;
    },

    getMarketPrice: (ticker: string): number | null => {
      void get().version; // re-derive on price updates
      const market = _markets.get(ticker);
      if (!market) return null;
      if (market.yesBid != null && market.yesAsk != null) {
        return Math.round((market.yesBid + market.yesAsk) / 2);
      }
      return market.lastPrice ?? null;
    },

    getMarket: (ticker: string): HydraMarket | null => {
      // Not subscribed to `version` — describeMarket is called from
      // synchronous render code (PositionTracker, TraderDetail, …) and
      // doesn't need the value to update reactively on every tick. The
      // title field is effectively static for a given ticker.
      return _markets.get(ticker) ?? null;
    },

    getEventStartTimeMsByTicker: (ticker: string): number | null => {
      void get().version;
      const market = _markets.get(ticker);
      if (!market) return null;
      const ck = market.canonicalGameKey;
      if (ck) {
        const cached = _startTimeMsByGameKey.get(ck);
        if (cached != null) return cached;
      }
      if (!market.eventStartTime) return null;
      const ms = Date.parse(market.eventStartTime);
      return Number.isFinite(ms) ? ms : null;
    },

    getConsensusFor: (canonicalGameKey): HydraConsensus | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      return _consensusByGameKeyAndPeriod.get(consensusCacheKey(canonicalGameKey, 1)) ?? null;
    },

    seedEventStartTimeMsByKey: (gameKey, ms) => {
      // Update policy lives in `updateStartTimeCache`. Pre-game, Unabated
      // competes with Kalshi/Poly via last-write-wins; once the game has
      // started, Unabated locks the value (Kalshi/Poly often keep streaming
      // the original scheduled tip even after live delays move the real
      // start, so Unabated's in-progress timing wins).
      if (updateStartTimeCache(gameKey, ms, 'unabated')) {
        _dirty = true;
        scheduleTick();
      }
    },

    getLastTradeYesCents: (ticker: string): number | null => {
      void get().version; // re-derive on tick updates
      return _markets.get(ticker)?.lastPrice ?? null;
    },
  };
});

/** Reactive read of the latest Full Game consensus for a canonical game key.
 *  Re-renders only when the cached snapshot reference changes (i.e. on a real
 *  prob shift) — not on every market-frame tick — because Zustand compares by
 *  reference and the cache only writes when probs actually move. */
export function useConsensusFor(canonicalGameKey: string | null | undefined): HydraConsensus | null {
  return useMarketStore((s) => s.getConsensusFor(canonicalGameKey));
}
